import axios, { type AxiosInstance } from 'axios'
import { approveQueue, askQueue, queueApproved } from '../lib/queue'
import { fmtTime } from '../lib/format'

const API_BASE = '/api/v1'

// Header the controller requires on state-changing requests (CSRF protection)
export const CSRF_HEADERS = { 'X-Requested-With': 'xcontrol' }

// Fired when the session is missing or expired; AuthGate shows the login page
export const UNAUTHORIZED_EVENT = 'xc:unauthorized'

// API Client
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    ...CSRF_HEADERS,
  },
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }
    return Promise.reject(error)
  },
)
offerQueue(api)
askClaim(api)

// MachinePCI: the functions on a machine's bus as its host reported them, named
export interface PCIFunctionView {
  address: string
  class?: string
  class_name?: string
  pci_id: string
  pci_subsys_id?: string
  vendor?: string
  device?: string
  product?: string // the subsystem's name: the card
  driver?: string
  link?: string // the trained PCI Express link, e.g. "8.0 GT/s PCIe x8"
  netdevs?: string[]
  kernel?: string[]
  known: boolean
}
// one line of a machine's logbook
export interface LogbookEntry {
  time: string
  kind: 'hardware' | 'state' | 'error' | 'action' | 'job'
  severity: 'info' | 'warn' | 'error'
  part?: string // pci, pcie, nic, disk, memory, ..., power, bmc-login
  subject?: string
  text: string
  by?: string
  source?: string
  job_id?: number
}

export interface MachinePCI { from?: string; at?: string; read: boolean; functions: PCIFunctionView[]; bmc?: BMCPCIeDevice[]; bmc_at?: string }

// a PCI Express device as the BMC lists it (its Redfish inventory, no boot needed)
export interface BMCPCIeDevice {
  id: string
  manufacturer?: string
  model?: string
  class?: string // NetworkController, DisplayController, ...
  address?: string
  pci_id?: string
  slot?: string
  location?: string // the board's name for it: CX-8#5
  lanes?: string
  serial?: string
  health?: string
}

// Claim: who works on a machine (its open maintenance session)
export interface Claim {
  session_id: number
  who: string
  by?: string
  what: string
  since: string
  explicit?: boolean
}

// claimMine: the claim is the viewer's own (Claude's accounts work as "Claude")
export const claimMine = (c: Claim, username?: string) => !!username && (c.who === username || (c.by || '').split(' ')[0] === username)

// askClaim: a change to a machine someone else works on is asked first (Martin,
// 2026-11-02): who, on what, since when - OK sends it again with X-Claim-Ack.
function askClaim(client: AxiosInstance) {
  client.interceptors.response.use((r) => r, async (error) => {
    const { response: res, config } = error
    const claim = res?.data?.claim as Claim | undefined
    if (res?.status !== 409 || !claim || !config || config.headers?.['X-Claim-Ack']) throw error
    if (!window.confirm(`${claim.who} is working on this machine: ${claim.what} (since ${fmtTime(claim.since)}).\n\nDo it anyway? ${claim.who} sees your change in the machine's jobs.`)) throw error
    config.headers['X-Claim-Ack'] = String(claim.session_id)
    return client.request(config)
  })
}

// offerQueue: whatever the zone lock or another job keeps from starting can go
// to the queue, for every action (lib/queue.ts): the request is sent again
// with ?queue=1 when that was confirmed or the answer to the question is yes.
function offerQueue(client: AxiosInstance) {
  client.interceptors.response.use((r) => r, async (error) => {
    const { response: res, config } = error
    if (res?.status !== 409 || !res.data?.queueable || !config || config.params?.queue) throw error
    if (!queueApproved() && !(await askQueue(res.data.error))) throw error
    return client.request({ ...config, params: { ...config.params, queue: 1 } })
  })
}

// changeLocked: the machine's protection stops updates and restarts too,
// because nothing stands in for it. With a group they go one at a time.
export const changeLocked = (m: { protected?: boolean; availability_group?: unknown }) => !!m.protected && !m.availability_group

// confirmJob asks before an action that starts a job on the machine. When it
// cannot start now the question says so, and "OK" adds it to the queue - asked
// once, no second question.
export async function confirmJob(machineId: string, text: string): Promise<boolean> {
  const lock = await machineApi.zoneLock(machineId).catch(() => undefined)
  text += '\n\nThe machine shows you as working on it until your jobs are done.'
  if (!lock || lock.can_start) return window.confirm(text)
  const ok = window.confirm(`${text}\n\nIt cannot start now: ${lock.reason}.\nOK adds it to the queue: it starts by itself when that is done - if that one fails, this one does not start.`)
  if (ok) approveQueue()
  return ok
}

// Auth API (served under /auth, not /api/v1)
export interface Me {
  username: string
  role: string // admin (owner), sysadmin, user (reader), customer (customer/project roles only)
  source: string
  bindings?: { role: string; scope_type: 'customer' | 'project'; scope_id: string }[]
  time_zone?: string // the zone the UI shows times in (the sites' local time)
}

export const authApi = {
  me: async (): Promise<Me> => {
    const response = await axios.get<Me>('/auth/me', { withCredentials: true })
    return response.data
  },

  logout: async () => {
    await axios.post('/auth/logout', null, { withCredentials: true, headers: CSRF_HEADERS })
  },

  localLogin: async (username: string, password: string): Promise<Me> => {
    const response = await axios.post<Me>(
      '/auth/local/login',
      { username, password },
      { withCredentials: true, headers: { 'Content-Type': 'application/json', ...CSRF_HEADERS } },
    )
    return response.data
  },
}

// Types
export interface Agent {
  id: string
  hostname: string
  kind?: 'site' | 'uplink' | 'node' | 'maintenance'
  ip_address: string
  status: string
  cpu_cores: number
  memory_total_mb: number
  disk_total_gb: number
  cpu_percent: number
  memory_used_mb: number
  disk_used_gb: number
  is_exit_node: boolean
  features?: string // JSON array, e.g. ["provisioner"]
  online?: boolean // heartbeat within the last 2 minutes (computed by the server)
  heartbeat_age_seconds?: number // -1 = never
  hardware?: HardwareSummary
  inventory_at?: string
  machine_id?: string
  machine_name?: string
  site_id?: string // the site it is at (a provisioner serves its site)
  security_updates?: number // pending security package updates
  pending_updates?: number
  credential?: 'own' | 'shared' // per-agent token or still the shared one
  last_heartbeat: string
  created_at: string
}

// CPUGeneration: short model and microarchitecture generation of the CPU
export interface CPUGeneration {
  short: string // "EPYC 7313P"
  name?: string // "Milan"
  arch?: string // "Zen 3"
  tier?: 'old' | 'mid' | 'current'
}

export interface HardwareSummary {
  product?: string
  cpu: string
  cpu_gen?: CPUGeneration
  memory_gb: number
  root_disk?: string
  root_used_percent?: number
  disks: number
  nics: number
  text: string
}

export interface Inventory {
  collected_at: string
  system: {
    vendor?: string; product?: string; serial?: string; board_vendor?: string; board_product?: string
    bios_vendor?: string; bios_version?: string; bios_date?: string
  }
  cpu: { model?: string; sockets: number; cores: number; threads: number }
  memory: {
    total_mb: number
    modules?: { locator?: string; size_mb: number; type?: string; speed_mts?: number; manufacturer?: string; part_number?: string; serial?: string }[]
  }
  disks?: { name: string; size_bytes: number; model?: string; serial?: string; rotational: boolean; transport?: string; root?: boolean; firmware?: string; controller?: string }[]
  root?: { device: string; mount: string; size_bytes: number; used_bytes: number }
  nics?: {
    name: string; kind: string; mac?: string; driver?: string; speed_mbps?: number; link: boolean; addresses?: string[]
    firmware?: string; firmware_pkg?: string; pci_address?: string; pci_id?: string; pci_subsys_id?: string
    master?: string; mtu?: number; vlan?: number; parent?: string; bond_mode?: string
  }[]
  // the host's network configuration as the agent reads it (read only)
  network?: {
    hostname?: string; fqdn?: string
    routes?: { destination: string; gateway?: string; device?: string; metric?: number; protocol?: string; source?: string }[]
    nameservers?: string[]; search_domains?: string[]
  }
  os: { name?: string; kernel?: string }
  bmc?: {
    model?: string; firmware_version?: string; redfish_version?: string; power_state?: string
    secure_boot?: string; boot_targets?: string[]; https_boot_certs: boolean
    firmware?: { name: string; version: string }[]
    interfaces?: { id: string; name?: string; mac?: string; enabled?: boolean; link?: string; ipv4?: string[] }[]
    accounts?: { id: string; user_name: string; role?: string; enabled: boolean; locked?: boolean }[]
  }
  warnings?: string[]
}

// bmcNICs: the BMC's own MACs recorded on a machine (purpose oob) - the
// dedicated port first ("bmc-eth0", or "bmc" from the discovery)
export function bmcNICs(m: Machine) {
  const rank = (name: string) => (name === 'bmc' || name === 'bmc-eth0' ? 0 : 1)
  return (m.nics || []).filter((n) => n.purpose === 'oob').sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name))
}

export function agentFeatures(agent: Agent): string[] {
  try {
    const parsed = JSON.parse(agent.features || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export interface VM {
  id: string
  name: string
  agent_id: string
  vcpus: number
  memory_mb: number
  disk_size_gb: number
  state: string
  status: string
  network_mode: string
  project_id?: string
  created_at: string
  updated_at: string
}

export interface CreateVMRequest {
  name: string
  agent_id: string
  vcpus: number
  memory_mb: number
  disk_size_gb: number
  network_mode: string
  project_id?: string
}

// Agent API
// The agent's own journal as the agent last reported it; asking also requests
// fresh lines, which arrive with its next heartbeat (pending until then)
export interface AgentLogs {
  agent_id: string
  hostname: string
  logs: { line: string }[]
  count: number
  collected_at?: string | null
  requested_at: string
  pending: boolean
  online: boolean
}

// The agent release the system agents update themselves to, and where each agent stands
export interface AgentRelease {
  release: { version: string; built?: string; files: Record<string, { name: string; sha256: string; size: number }> } | null
  agents: {
    id: string
    hostname: string
    site_id?: string
    version: string
    platform?: string
    online: boolean
    mgmt?: boolean // the site's mgmt agent: keeps the release for its site
    release_cached?: string
    state?: 'updating' | 'updated' | 'failed' | 'rolled-back'
    from?: string
    to?: string
    error?: string
    at?: string
    hold?: boolean
    advice: string // what happens next
  }[]
}

export const agentApi = {
  release: async (): Promise<AgentRelease> => (await api.get<AgentRelease>('/agent-release')).data,
  // hold an agent at its version; retry clears a failed update
  updateHold: async (id: string, hold: boolean, retry = false) => (await api.put(`/agents/${id}/update-hold`, { hold, retry })).data,

  logs: async (id: string, lines = 100): Promise<AgentLogs> => (await api.get<AgentLogs>(`/agents/${id}/logs`, { params: { lines } })).data,

  list: async (): Promise<Agent[]> => {
    const response = await api.get<Agent[]>('/agents')
    return response.data
  },
  
  get: async (id: string): Promise<Agent> => {
    const response = await api.get<Agent>(`/agents/${id}`)
    return response.data
  },
  
  resetCredential: async (id: string) => {
    const response = await api.delete(`/agents/${id}/credential`)
    return response.data
  },

  approve: async (id: string) => {
    const response = await api.post(`/agents/${id}/approve`)
    return response.data
  },
  
  reject: async (id: string) => {
    const response = await api.post(`/agents/${id}/reject`)
    return response.data
  },
  
  delete: async (id: string) => {
    await api.delete(`/agents/${id}`)
  },

  inventory: async (id: string): Promise<Inventory> => {
    const response = await api.get<Inventory>(`/agents/${id}/inventory`)
    return response.data
  },

  createMachine: async (id: string): Promise<Machine> => {
    const response = await api.post<Machine>(`/agents/${id}/machine`)
    return response.data
  },
}

// VM API
export const vmApi = {
  list: async (): Promise<VM[]> => {
    const response = await api.get<VM[]>('/vms')
    return response.data
  },
  
  get: async (id: string): Promise<VM> => {
    const response = await api.get<VM>(`/vms/${id}`)
    return response.data
  },
  
  create: async (data: CreateVMRequest): Promise<VM> => {
    const response = await api.post<VM>('/vms', data)
    return response.data
  },
  
  start: async (id: string) => {
    const response = await api.post(`/vms/${id}/start`)
    return response.data
  },
  
  stop: async (id: string) => {
    const response = await api.post(`/vms/${id}/stop`)
    return response.data
  },
  
  delete: async (id: string) => {
    await api.delete(`/vms/${id}`)
  },
  
  getVNC: async (id: string) => {
    const response = await api.get(`/vms/${id}/vnc`)
    return response.data
  },
  
  getLogs: async (id: string) => {
    const response = await api.get(`/vms/${id}/logs`)
    return response.data
  },
}

// User API
export interface User {
  id: string
  username: string
  email: string
  full_name: string
  role: string
  active: boolean
  auth_source?: string
  customer_id?: string // a user of this customer
  customer_roles?: boolean // a platform account that may also hold customer and project roles
  last_login?: string
  created_at: string
}

export type CustomerUser = User & { roles: RoleBinding[] }

export const customerUserApi = {
  listAll: async (): Promise<CustomerUser[]> => (await api.get('/customer-users')).data,
  list: async (customerId: string): Promise<CustomerUser[]> => (await api.get(`/customers/${customerId}/users`)).data,
  create: async (customerId: string, u: { username: string; email: string; full_name?: string; customer_admin?: boolean }): Promise<User> => (await api.post(`/customers/${customerId}/users`, u)).data,
  remove: async (customerId: string, userId: string) => (await api.delete(`/customers/${customerId}/users/${userId}`)).data,
}

export const userApi = {
  setCustomerRoles: async (id: string, allowed: boolean): Promise<User> => (await api.put<User>(`/users/${id}/customer-roles`, { allowed })).data,
  list: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users')
    return response.data
  },
  
  create: async (data: { username: string; email: string; password: string; full_name?: string; role?: string }) => {
    const response = await api.post('/users', data)
    return response.data
  },
  
  // a local account's email, name, role and whether it may log in
  update: async (id: string, data: { email?: string; full_name?: string; role?: string; active?: boolean }): Promise<User> =>
    (await api.put<User>(`/users/${id}`, data)).data,
  // a local account's new password (the old one stops working)
  setPassword: async (id: string, password: string) => (await api.post(`/users/${id}/password`, { password })).data,

  delete: async (id: string) => {
    await api.delete(`/users/${id}`)
  },
}

// Projects: one API for staff and customers (docs/API.md); every project
// belongs to a customer, the platform customer's projects are system projects
export interface ProjectQuota {
  cpus: number
  memory_mb: number
  disk_gb: number
  vms: number
  public_ips: number
}

export interface Project {
  id: string
  name: string
  description: string
  customer_id: string
  customer_name: string
  customer_code: string
  customer_kind: CustomerKind
  system: boolean
  active: boolean
  created_at: string
  role: 'project-admin' | 'project-member' | 'project-reader'
  staff: boolean
  needs_reason: boolean
  can_admin: boolean
  can_create_projects: boolean
  can_manage: boolean
  quota: ProjectQuota
  usage: { cpus: number; memory_mb: number; disk_gb: number; vms: number }
  servers: number
  machines: number
  networks: number
  members: number
  admins?: string[]
  clusters?: string[]
}

export type ProjectInput = Partial<Pick<Project, 'id' | 'name' | 'description' | 'customer_id' | 'active'>> & { quota?: Partial<ProjectQuota> }

export const projectApi = {
  list: async (customerId?: string): Promise<Project[]> =>
    (await api.get<Project[]>('/projects', { params: customerId ? { customer_id: customerId } : {} })).data,
  get: async (id: string): Promise<Project> => (await api.get<Project>(`/projects/${id}`)).data,
  create: async (data: ProjectInput): Promise<Project> => (await api.post<Project>('/projects', data)).data,
  update: async (id: string, data: ProjectInput): Promise<Project> => (await api.put<Project>(`/projects/${id}`, data)).data,
  delete: async (id: string) => {
    await api.delete(`/projects/${id}`)
  },
}

// Customers own projects; kind platform is the platform itself (system projects)
export type CustomerKind = 'external' | 'internal' | 'platform'

export interface Customer {
  id: string
  code: string
  name: string
  kind: CustomerKind
  status: 'active' | 'suspended' | 'terminated'
  contacts?: string
  contract_ref?: string
  notes?: string
  created_by?: string
  created_at: string
  projects: number
  dedicated_machines: number
  vms: number
  networks: number
}

export const customerApi = {
  list: async (): Promise<Customer[]> => (await api.get<Customer[]>('/customers')).data,
  get: async (id: string): Promise<Customer> => (await api.get<Customer>(`/customers/${id}`)).data,
  save: async (c: Partial<Customer>, isNew: boolean): Promise<Customer> =>
    isNew ? (await api.post<Customer>('/customers', c)).data : (await api.put<Customer>(`/customers/${c.id}`, c)).data,
  remove: async (id: string) => {
    await api.delete(`/customers/${id}`)
  },
}

// Customer and project roles of people (platform roles come from Kanidm groups)
export type ScopedRole = 'customer-admin' | 'project-admin' | 'project-member' | 'project-reader'

export interface RoleBinding {
  id: number
  subject_type: string
  subject: string
  scope_type: 'customer' | 'project'
  scope_id: string
  scope_name?: string
  customer_id?: string
  role: ScopedRole
  created_by: string
  created_at: string
}

export const roleBindingApi = {
  list: async (params: { scope_type?: string; scope_id?: string; subject?: string; customer_id?: string } = {}): Promise<RoleBinding[]> =>
    (await api.get<RoleBinding[]>('/role-bindings', { params })).data,
  save: async (b: { subject: string; role: ScopedRole; scope_type: string; scope_id: string }): Promise<RoleBinding> =>
    (await api.post<RoleBinding>('/role-bindings', b)).data,
  remove: async (id: number) => {
    await api.delete(`/role-bindings/${id}`)
  },
}

export interface AuditEvent {
  id: number
  time: string
  actor: string
  role: string
  source: string
  method: string
  path: string
  status: number
  resource_type: string
  resource_id?: string
  customer_id?: string
  project_id?: string
  ip?: string
  detail?: string
}

export const auditApi = {
  list: async (params: { actor?: string; resource_type?: string; resource_id?: string; customer_id?: string; project_id?: string; before?: number; limit?: number } = {}): Promise<AuditEvent[]> =>
    (await api.get<AuditEvent[]>('/audit', { params })).data,
}

// Image API
export interface Image {
  id: string
  name: string
  description: string
  format: string
  size_bytes: number
  os_type: string
  os_distribution: string
  public: boolean
  status: string
  created_at: string
}

export const imageApi = {
  list: async (): Promise<Image[]> => {
    const response = await api.get<Image[]>('/images')
    return response.data
  },
  
  delete: async (id: string) => {
    await api.delete(`/images/${id}`)
  },
}

// Health Check
// Transceiver: the module in a port's cage, with its own measurements
// (optics with diagnostics: received and sent light, temperature, alarms)
export interface Transceiver {
  present: boolean
  type?: string
  vendor?: string
  pn?: string
  length?: string
  media?: string
  rx_power?: string
  tx_power?: string
  temperature?: string
  alarms?: string[]
}

// Machine API (bare-metal inventory, power, installation)
export interface MachineNIC {
  id?: number
  mac_address: string
  name: string
  purpose: string // data, mgmt, provisioning
}

export interface Machine {
  id: string
  name: string
  class: string
  role: string
  state: string
  power_driver: string
  boot_driver: string
  console_driver: string
  notes?: string
  // protected: xControl refuses everything that can disturb the running host
  protected?: boolean
  protected_reason?: string
  nics?: MachineNIC[]
  nic_locations?: Record<string, string[]> // MAC -> switch ports
  firmware_updates?: number // components with a newer approved image
  firmware_pending?: number // components with a newer fitting image that waits for approval
  bmc_user?: string // the BMC account xControl logs in with
  // the BMC credentials: worked (a login succeeded) / refused (wrong user or password); neither: not verified yet
  bmc_login_ok_at?: string
  bmc_login_failed_at?: string
  bmc_login_error?: string
  bmc_spare_user?: string // a second BMC login xControl keeps (e.g. the factory account with its new password)
  bmc_spare_at?: string
  os_user?: string // the emergency login of the installed system (console)
  os_password_at?: string
  os_password_by?: string
  staged_bios?: string // BIOS flashed but not active yet (needs a boot)
  firmware?: FirmwareState[] // BMC and BIOS: installed, offered, pending
  firmware_security?: number // of those, updates that fix security issues
  cluster_id?: string // the cluster it serves - comes with the role
  zone_id?: string // its availability zone, one of its cluster's
  site_id?: string // where it is: a datacenter or a warehouse (always set)
  site_moved_at?: string // xControl found its BMC at another site and moved it there
  site_moved_from?: string
  rack_id?: string // the rack it is mounted in (at its site)
  rack_u?: number // the lowest height unit it takes, counted from the bottom
  rack_units?: number // how many units it takes
  rack_face?: 'front' | 'rear' // the side its front faces (switches often face the rear)
  rack_half?: boolean // half depth: takes only its side of the units
  device_type_id?: string // its device type (chassis and slots)
  decommissioned_at?: string // taken out of its cluster (role and cluster gone)
  functions?: MachineFunction[] // what it does for its site's infrastructure
  protected_auto?: boolean // the protection comes from its site functions
  decommissioned_from?: string // "<cluster> / <role>"
  erase_pending?: 'quick' | 'full' // the decommissioning asked for the disks to be erased (the inspection does it)
  erased_at?: string // when an erase job last succeeded
  security_updates?: number // pending security package updates of the OS
  pending_updates?: number // every pending package update of the OS
  // whether the host's clock follows a source, as its agent reports it; stale: no report for a while
  clock?: { synced: boolean; text: string; since?: string; at: string; stale?: boolean }
  hardware?: HardwareSummary
  inventory_at?: string
  inventory_source?: string
  bmc_address?: string
  agent?: { id: string; hostname: string; status: string; online: boolean; maintenance?: boolean }
  node_agent?: { id: string; hostname: string; status: string; online: boolean } // only reports, runs no jobs
  // the job that runs on it: what it does (action e.g. bmc-login-check, power-off,
  // disk-erase, bios-setup-apply, os-install; detail the words to it), who started it when
  active_job?: { id: number; kind: string; state: string; action?: string; detail?: string; created_by?: string; created_at?: string; started_at?: string; queued_behind?: number; in_queue?: number }
  power_op?: MachinePowerOp // switching through the PDU outlets, running or ended in the last 30 minutes
  // the running inspection: its current step (the first not done) and that step's job while it runs
  claim?: Claim // who works on the machine; another person's change is asked first
  inspection?: { id: number; kind?: 'firmware'; step_no: number; steps: number; step: string; step_state: 'pending' | 'running'; job?: number; created_by: string; created_at: string }
  last_job?: { id: number; kind: string; state: string; error?: string; action?: string; detail?: string; created_by?: string; created_at?: string }
  // who stands in for it: updates and restarts one at a time, when the others are up
  availability_group?: { id: number; name: string; needed_up: number; members: number; can_change: boolean; why?: string }
  addresses?: MachineAddress[] // addresses in the cluster networks (BMC, host)
  host_addresses?: { interface: string; address: string }[] // reported by the agent on the machine (read only)
  usage?: MachineUsage // what the machine is used for
  project_id?: string // dedicated and reserved machines
  // lifecycle: derived by the controller (docs/MACHINES.md)
  lifecycle?: 'discovered' | 'inspected' | 'ready' | 'reserved' | 'installing' | 'maintenance' | 'in-use' | 'error'
  zone?: string // availability zone, or "cluster:<id>" without one
  power_state?: string // as the BMC last reported it (on, off, ...)
  power_at?: string
  power_source?: 'bmc' | 'agent' // who reported power_state: the BMC or the host's own agent (it runs)
  identify?: 'on' | 'off' // the identify LED as the last identify job left it
  identify_at?: string
  // the firmware's own boot order (AMI) as the last BMC measurement read it
  boot_order?: { name: string; reference?: string; enabled: boolean }[]
  boot_order_at?: string
  // whether the BIOS reports its settings to the BMC (read in the BIOS setup
  // along with every setup job); Disabled: Redfish's BIOS values are out of date
  bios_redfish?: 'Enabled' | 'Disabled'
  bios_redfish_at?: string
  // what it boots first: Redfish's order while the BIOS reports to it, else
  // the BIOS setup reading (machine page only)
  boot_first?: { name: string; network: boolean; source: 'redfish' | 'setup'; at?: string; pending?: boolean; pending_stuck?: boolean }
  boot_order_pending_since?: string
  created_at: string
  updated_at: string
}

// BMCCapability: what a BMC driver can (pkg/platform/bmc.go)
export type BMCCapability = 'web-api' | 'console-login' | 'kvm' | 'bios-redfish' | 'boot-override' | 'boot-flags-ipmi' |
  'fixed-boot-order' | 'firmware-dual-image' | 'firmware-redfish' | 'account-cold-reset' | 'account-self-password' | 'sol' | 'virtual-media'

export const bmcCapabilityLabel: Record<BMCCapability, string> = {
  'web-api': "the BMC's own web API (sessions, host BIOS version, interface switches)",
  'console-login': "logging in to the BMC's web UI through the console proxy",
  kvm: 'reading the graphical console (screenshots, the BIOS setup over it)',
  'bios-redfish': 'BIOS attributes through Redfish',
  'boot-override': 'one-time boot through Redfish',
  'boot-flags-ipmi': 'one-time boot through IPMI',
  'fixed-boot-order': 'the boot order as a BIOS attribute',
  'firmware-dual-image': 'dual-image BMC firmware updates',
  'firmware-redfish': "firmware updates through Redfish's update service",
  'account-cold-reset': 'a cold reset before account changes',
  'account-self-password': 'accounts setting their own password',
  sol: 'serial over LAN',
  'virtual-media': 'virtual media',
}

// MachinePlatform: the platform profiles that matched a machine (general
// first) and what they say together (docs/PLATFORMS.md)
export interface MachinePlatform {
  identity: { bmc_vendor?: string; bmc_family?: string; system_vendor?: string; system_model?: string; board?: string; bios_vendor?: string; bios_version?: string; cpu_generation?: string }
  layers: string[]
  title: string
  // the BMC driver of the family and what it can (docs/PLATFORMS.md, "BMC drivers")
  bmc: { driver?: string; with?: string[]; without?: string[]; capabilities: BMCCapability[] }
  console: { serial?: string }
  bios: { setup?: string; sync_switch?: string; settings?: SetupSettingDef[] }
  boot: { driver?: string; new_install_entry?: string }
  firmware: { component: string; drivers?: string[]; models?: string[]; method: string; activation?: string; note?: string }[]
  notes?: string[]
  // what jobs learned about this kind of machine beyond the profile (key: account.create.oem, ...)
  learned?: { key: string; value: string; bmc_firmware: string; machine_id: string; job_id: number; first_seen: string; last_seen: string }[]
  // what the platform cannot do, function → why: a capability, setup-console, flash-bmc, flash-bios
  lacking?: Record<string, string>
}

export type MachineUsage = 'platform' | 'capacity' | 'dedicated' | 'pool' | 'reserved'

export interface MachineAddress {
  network_id: string
  network: string
  plane: string // network purpose: oob-bmc, oob-switch, mgmt, data
  purpose: string // bmc, host, ...
  ip: string
}

export interface DiscoveredDevice {
  id: number
  agent_id: string
  agent_hostname: string
  ip: string
  mac?: string
  interface?: string
  sources: string
  name_hint?: string
  pdu?: string // the recorded PDU with this MAC or address
  pdu_network?: boolean // the address is in a PDU network
  is_bmc: boolean
  bmc_vendor?: string
  bmc_product?: string
  bmc_uuid?: string
  redfish_version?: string
  first_seen: string
  last_seen: string
  stale: boolean
  machine_id?: string
  machine_name?: string
  suggested_host_macs?: string[]
  same_as?: string
  host_ports?: string[] // switch ports of the server this BMC belongs to
  host_macs_from_switch?: boolean // suggested_host_macs were learned on those ports
  location?: string[] // switch ports where this device's MAC is seen
  suggested_name?: string // the generic name it gets when added
}

export const discoveryApi = {
  list: async (): Promise<DiscoveredDevice[]> => {
    const response = await api.get<DiscoveredDevice[]>('/discovery')
    return response.data
  },
  // a machine without role of a discovered BMC; the identification starts right away
  add: async (id: number, body: { name?: string; username: string; password: string }): Promise<{ machine_id: string; name: string; job_id: number; note?: string }> =>
    (await api.post(`/discovery/${id}/add`, body)).data,
  // "Scan now": every site's active agent runs discovery, switch reading and port probe at once
  scans: async (): Promise<DiscoveryScan[]> => (await api.get<DiscoveryScan[]>('/discovery/scan')).data,
  scan: async (): Promise<DiscoveryScan[]> => (await api.post<DiscoveryScan[]>('/discovery/scan')).data,
}

// DiscoveryScan: a scan someone asked for; each part is done when its report arrived after it
export interface DiscoveryScan {
  id: number
  site_id: string
  agent_id: string
  agent_hostname: string
  by: string
  requested_at: string
  sent_at?: string
  discovery_at?: string
  switches_at?: string
  probe_at?: string
  probes: boolean
  state: 'running' | 'done' | 'incomplete'
}

// Site functions: mgmt-uplink (remote access), mgmt-backup (backup access over
// the main uplink), agent (site agent; passive: the standby), provisioner (the
// active agent's job); temporary: a stand-in until the planned machine takes over
export interface MachineFunction {
  name: 'mgmt-uplink' | 'mgmt-backup' | 'agent' | 'provisioner'
  temporary?: boolean
  passive?: boolean
}

// What every datacenter needs, and what its infrastructure has of it
export interface ReadinessItem {
  key: string
  title: string
  state: 'ok' | 'temporary' | 'missing'
  machines?: string[]
  note?: string
}

export interface LLDPNeighbor {
  chassis_id?: string
  port_id?: string
  port_descr?: string
  system_name?: string
  system_descr?: string
  mgmt_addr?: string
}

// A NIC and where it is connected: ours (a switch xControl reads), outside
// (another device answers), unknown (link, nobody tells), no-link, unseen
export interface NICConnection {
  name?: string
  mac: string
  purpose?: string
  driver?: string
  pci_id?: string
  speed_mbps?: number
  link?: boolean
  link_note?: string // e.g. only at a fixed 10G (a 10G cable in a 25G port)
  down?: boolean // the host has the port off: it cannot tell whether a cable has link
  slot?: string // expansion slot of the card (PCIE1, OCP); empty onboard
  pci_address?: string
  pci_subsys_id?: string
  firmware?: string
  neighbor?: LLDPNeighbor
  neighbor_at?: string // set when the neighbour is the one last heard (the newest inventory heard none)
  link_check?: { at: string; speed_mbps?: number } // the host keeps it off: a port check switched it on and saw this
  switch_ports?: string[]
  switch_speed?: string // what the switch runs the port at, also while the host keeps it off
  link_warning?: string // what the rules for port speeds say
  where: 'ours' | 'outside' | 'unknown' | 'no-link' | 'empty' | 'unseen'
  transceiver?: Transceiver // what sits in the cage
}

export interface NICConnections {
  source?: string // the agent whose inventory gives the host's view
  at?: string
  nics: NICConnection[]
}

export interface CreateMachineRequest {
  name: string
  class: string
  role?: string
  site?: string
  site_id?: string // where it is (default: the cluster's site)
  cluster_id?: string
  power_driver: string
  power_config?: Record<string, unknown>
  notes?: string
  nics: MachineNIC[]
  bmc_dns_name?: string
}

export interface ProvisionRequest {
  agent_id: string
  ip: string
  prefix?: number
  gateway?: string
  dns?: string[]
  boot_mode?: string
  hostname?: string
  username: string // for pasted keys (ssh_authorized_keys); library keys bring their owners' names
  ssh_authorized_keys: string
  ssh_key_ids?: number[] // keys of the library: a user per owner (xControl's own key comes always)
  disk_match?: string
  timezone?: string
  keyboard?: string
  disable_secure_boot?: boolean
  dns_name?: string
  install_agent?: boolean
  network_id?: string
  // a public address of the pool behind a transit address on the public VLAN, as a VM has one
  // (on the management NIC when it is the machine's only port, mgmt-data)
  public?: boolean
}

// The platform's SSH key library: the operators' public keys and xControl's
// own; an installation puts the chosen ones on the machine, a user per owner
export interface PlatformSSHKey {
  id: number
  owner: string
  name: string
  public_key: string
  fingerprint: string
  created_at: string
  system?: boolean // xControl's own key (user xcontrol on every installed machine)
  linux_user: string // the user it gets on an installed machine
  mine?: boolean
}

export const sshKeyApi = {
  list: async (): Promise<PlatformSSHKey[]> => (await api.get('/ssh-keys')).data,
  add: async (body: { name?: string; public_key: string; owner?: string }): Promise<PlatformSSHKey> => (await api.post('/ssh-keys', body)).data,
  remove: async (id: number) => (await api.delete(`/ssh-keys/${id}`)).data,
}

export interface ProvisionJob {
  id: number
  machine_id: string
  agent_id: string
  kind?: string // install, inspect, bmc
  state: string // queued, pending, running, succeeded, failed, cancelled
  spec: string
  error?: string
  result?: string // JSON, e.g. BIOS attributes of a bmc job
  cancel_requested: boolean
  created_by: string
  created_at: string
  started_at?: string
  finished_at?: string
}

export interface ProvisionEvent {
  id: number
  job_id: number
  time: string
  kind: string
  mac?: string
  detail: string
}

export interface MachineRole {
  id: string
  class?: string
  description: string
}

export const machineApi = {
  roles: async (): Promise<MachineRole[]> => {
    const response = await api.get<MachineRole[]>('/machines/roles')
    return response.data
  },

  list: async (): Promise<Machine[]> => {
    const response = await api.get<Machine[]>('/machines')
    return response.data
  },

  // one machine with what only its own view carries (boot_first)
  get: async (id: string): Promise<Machine> => (await api.get<Machine>(`/machines/${id}`)).data,

  create: async (data: CreateMachineRequest) => {
    const response = await api.post('/machines', data)
    return response.data
  },

  delete: async (id: string) => {
    await api.delete(`/machines/${id}`)
  },

  // update changes fields of a machine, e.g. its usage and project
  update: async (id: string, data: { usage?: string; project_id?: string; notes?: string; role?: string; cluster_id?: string; protected?: boolean; protected_reason?: string }): Promise<Machine> => {
    const response = await api.put<Machine>(`/machines/${id}`, data)
    return response.data
  },

  powerStatus: async (id: string): Promise<{ power_state: string }> => {
    const response = await api.get(`/machines/${id}/power`)
    return response.data
  },

  powerAction: async (id: string, action: 'on' | 'off' | 'cycle') => {
    const response = await api.post(`/machines/${id}/power`, { action })
    return response.data
  },

  provision: async (id: string, data: ProvisionRequest): Promise<ProvisionJob> => {
    const response = await api.post<ProvisionJob>(`/machines/${id}/provision`, data)
    return response.data
  },

  inspect: async (id: string, agent_id: string): Promise<ProvisionJob> => {
    const response = await api.post<ProvisionJob>(`/machines/${id}/inspect`, { agent_id })
    return response.data
  },

  inventory: async (id: string): Promise<Inventory> => {
    const response = await api.get<Inventory>(`/machines/${id}/inventory`)
    return response.data
  },

  // every function on the machine's bus (PCI Express devices too), named; unknown IDs marked
  pci: async (id: string): Promise<MachinePCI> => (await api.get<MachinePCI>(`/machines/${id}/pci`)).data,
  // the machine's logbook: hardware changes, power, refused logins, user actions, jobs - newest first
  logbook: async (id: string, kind = 'all', before?: string): Promise<LogbookEntry[]> =>
    (await api.get<LogbookEntry[]>(`/machines/${id}/logbook`, { params: { kind, before } })).data,

  // where the NICs are connected: the host's view (link, LLDP neighbour) and the switches'
  nicConnections: async (id: string): Promise<NICConnections> => (await api.get<NICConnections>(`/machines/${id}/nic-connections`)).data,
  // where it is mounted: a rack at its site, the lowest unit, the height; rack_id '' takes it out
  mount: async (id: string, m: { rack_id: string; u?: number; units?: number; face?: 'front' | 'rear'; half?: boolean }) => (await api.put(`/machines/${id}/rack`, m)).data,

  jobs: async (id: string): Promise<ProvisionJob[]> => {
    const response = await api.get<ProvisionJob[]>(`/machines/${id}/provision-jobs`)
    return response.data
  },

  // exportBMCCredentials: owners download all BMC credentials (CSV in the import format)
  exportBMCCredentials: async (clusterId?: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get('/machines/bmc-credentials', { params: clusterId ? { cluster_id: clusterId } : {}, responseType: 'blob' })
    const m = /filename="([^"]+)"/.exec(response.headers['content-disposition'] || '')
    return { blob: response.data as Blob, filename: m ? m[1] : 'xcontrol-bmc-credentials.csv' }
  },

  bmcImport: async (content: string, dryRun: boolean, inspect: boolean, names?: Record<string, string>): Promise<{ items: BMCImportItem[]; warnings?: string[] }> => {
    const response = await api.post('/machines/bmc-import', { content, dry_run: dryRun, inspect, names })
    return response.data
  },

  updateNIC: async (id: string, mac: string, data: { purpose?: string; name?: string }): Promise<MachineNIC> => {
    const response = await api.put<MachineNIC>(`/machines/${id}/nics/${encodeURIComponent(mac)}`, data)
    return response.data
  },

  // firmwareUpdates: pending firmware and OS security updates of all machines
  firmwareUpdates: async (): Promise<MachineUpdate[]> => {
    const response = await api.get<MachineUpdate[]>('/machines/firmware-status')
    return response.data
  },

  firmwareStatus: async (id: string): Promise<FirmwareState[]> => {
    const response = await api.get<FirmwareState[]>(`/machines/${id}/firmware-status`)
    return response.data
  },

  consoleShots: async (id: string): Promise<ConsoleShot[]> => {
    const response = await api.get<ConsoleShot[]>(`/machines/${id}/console-shots`)
    return response.data
  },

  takeConsoleShot: async (id: string, force = false): Promise<ConsoleShot> => {
    const response = await api.post<ConsoleShot>(`/machines/${id}/console-shots`, { force })
    return response.data
  },

  consoleShotImageURL: (id: string, shot: number) => `${API_BASE}/machines/${id}/console-shots/${shot}/image`,

  maintenanceBoot: async (id: string): Promise<ProvisionJob> => {
    const response = await api.post<ProvisionJob>(`/machines/${id}/maintenance-boot`, {})
    return response.data
  },

  // erase the disks in the maintenance system: quick zeros the first 10 GiB and
  // the last 16 MiB of every disk, full the whole disk; confirm is the machine's name
  erase: async (id: string, body: { mode: 'quick' | 'full'; confirm: string; device?: string }): Promise<ProvisionJob> =>
    (await api.post<ProvisionJob>(`/machines/${id}/erase`, body)).data,

  setFunctions: async (id: string, functions: MachineFunction[]): Promise<Machine> =>
    (await api.put<Machine>(`/machines/${id}/functions`, { functions })).data,

  // take a machine out of its cluster: role, cluster and usage go (confirm: its name)
  decommission: async (id: string, confirm: string, erase: 'quick' | 'full' | 'none'): Promise<{ decommissioned_from: string; inspection?: number; note?: string }> =>
    (await api.post(`/machines/${id}/decommission`, { confirm, erase })).data,

  maintenanceEnd: async (id: string): Promise<{ message: string }> => {
    const response = await api.post(`/machines/${id}/maintenance-end`, {})
    return response.data
  },

  // the BIOS settings of the machine's platform profile (docs/PLATFORMS.md)
  biosSetupSettings: async (machineId: string): Promise<SetupSettingDef[]> =>
    (await api.get<SetupSettingDef[]>('/bios-setup/settings', { params: { machine_id: machineId } })).data,
  platform: async (id: string): Promise<MachinePlatform> => (await api.get<MachinePlatform>(`/machines/${id}/platform`)).data,


  biosSetup: async (id: string, req: { settings?: { setting?: string; path?: string[]; value?: string }[]; save?: boolean; dump?: boolean; force?: boolean }): Promise<ProvisionJob> => {
    const response = await api.post<ProvisionJob>(`/machines/${id}/bios-setup`, req)
    return response.data
  },
  // the machine's setup dumps (newest first) and one with every item ("latest")
  biosDumps: async (id: string): Promise<BIOSSetupDump[]> => (await api.get<BIOSSetupDump[]>(`/machines/${id}/bios-setup/dumps`)).data,
  bmcHealth: async (id: string): Promise<BMCHealth> => (await api.get<BMCHealth>(`/machines/${id}/bmc-health`)).data,
  // a compute host: VPP on its data ports, the mirror for the VMs' disks, its VMs
  compute: async (id: string): Promise<ComputeView> => (await api.get<ComputeView>(`/machines/${id}/compute`)).data,
  applyComputeRole: async (id: string): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/compute/apply`)).data,
  prepareComputeDisks: async (id: string, confirm: string): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/compute/disks`, { confirm })).data,
  // a GPU host: NVIDIA's driver, Fabric Manager and nvlsm, the GPUs on vfio-pci (check: only read what runs)
  applyGPUHost: async (id: string, check: boolean): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/compute/gpu`, { check })).data,
  // a gateway's routing configuration as xControl renders it, and the difference to what it runs
  gatewayConfig: async (id: string): Promise<GatewayConfig> => (await api.get<GatewayConfig>(`/machines/${id}/gateway-config`)).data,
  // bring the gateway to what xControl renders (a job: commit-confirm, BGP checked, confirm); dry_run: only the commands
  applyGatewayConfig: async (id: string, dryRun: boolean): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/gateway-config/apply`, { dry_run: dryRun })).data,
  // the gateway's operating system shuts down: BGP and VRRP say goodbye (a job, the way back is Power on)
  startGatewayShutdown: async (id: string): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/gateway-shutdown`, {})).data,
  // a gateway's provider session shut or its links to the cluster down for some minutes, the traffic over the others (a job)
  startGatewayFailover: async (id: string, minutes: number, mode: 'session' | 'data-legs'): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/gateway-failover`, { minutes, mode })).data,
  // a gateway's system image: what it runs, what is on its disk, the newest biOS
  gatewayImage: async (id: string): Promise<GatewayImageView> => (await api.get<GatewayImageView>(`/machines/${id}/gateway-image`)).data,
  // the gateway restarts into an image on its disk, or into the newest, which it first adds (a job)
  startGatewayImage: async (id: string, version: string): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/gateway-image`, { version })).data,
  // replaces the stored BMC user and password (unverified until a login works; check: try the login
  // right away, identify: inspect the machine)
  // address: a machine without a BMC gets one (its IP)
  setBMCCredentials: async (id: string, body: { username: string; password: string; address?: string; check?: boolean; identify?: boolean }): Promise<{ machine_id: string; username: string; job_id?: number; note?: string }> =>
    (await api.put(`/machines/${id}/bmc-credentials`, body)).data,
  // the name by the naming scheme and the usage for a role
  assignInfo: async (id: string, role: string, clusterId?: string): Promise<{ roles: MachineRole[]; clusters: Cluster[]; cluster_id?: string; zones?: { id: string; name: string }[]; name?: string; usage?: string; class?: string }> =>
    (await api.get(`/machines/${id}/assign`, { params: { role, cluster_id: clusterId || undefined } })).data,
  assign: async (id: string, body: { role: string; cluster_id?: string; zone_id?: string; name?: string; usage?: string; project_id?: string }): Promise<{ machine_id: string; name: string; role: string; cluster_id: string; usage: string }> =>
    (await api.post(`/machines/${id}/assign`, body)).data,
  biosTimings: async (id: string, dump: number | 'latest'): Promise<BIOSTimings> => (await api.get<BIOSTimings>(`/machines/${id}/bios-setup/timings`, { params: { dump } })).data,
  measureBMC: async (id: string): Promise<ProvisionJob> => (await api.post<ProvisionJob>(`/machines/${id}/bmc-health`, {})).data,
  // two readings compared (default: the newest with the one before)
  biosDiff: async (id: string, q: { from?: number; to?: number; info?: boolean; help?: boolean } = {}): Promise<BIOSDumpDiff> =>
    (await api.get<BIOSDumpDiff>(`/machines/${id}/bios-setup/diff`, { params: q })).data,
  biosDump: async (id: string, dump: number | 'latest'): Promise<{ dump: BIOSSetupDump; items: AptioItem[]; can_change: boolean }> =>
    (await api.get(`/machines/${id}/bios-setup/dumps/${dump}`)).data,

  bmcConsole: async (id: string, agentId: string): Promise<{ url: string; expires_at: string }> => {
    const response = await api.post(`/machines/${id}/bmc-console`, { agent_id: agentId })
    return response.data
  },

  hostFirmware: async (id: string, req: { action: string; device: string; firmware_id?: number }): Promise<ProvisionJob> => {
    const response = await api.post<ProvisionJob>(`/machines/${id}/host-firmware`, req)
    return response.data
  },

  bmc: async (id: string, req: BMCActionRequest): Promise<ProvisionJob> => {
    const response = await api.post<ProvisionJob>(`/machines/${id}/bmc`, req)
    return response.data
  },
  // switch to the service account; with rotate the old account gets a new password (spare login)
  // queue: when the zone lock or another job keeps it from starting, queue it (starts when that one succeeded)
  secureBMC: async (id: string, req: { rotate: boolean; account?: string; queue?: boolean }): Promise<{ job: ProvisionJob; rotate?: string; queued?: boolean }> =>
    (await api.post<{ job: ProvisionJob; rotate?: string; queued?: boolean }>(`/machines/${id}/bmc-secure`, req)).data,
  // whether a maintenance job on the machine may start now, or would be queued behind what
  zoneLock: async (id: string): Promise<{ can_start: boolean; reason?: string; behind?: number }> =>
    (await api.get(`/machines/${id}/zone-lock`)).data,
  // owners; recorded in the audit log
  bmcCredentials: async (id: string): Promise<BMCCredentials> => (await api.get<BMCCredentials>(`/machines/${id}/bmc-credentials`)).data,
  // the serial console (IPMI SOL): a short session, its WebSocket URL
  openSOL: async (id: string): Promise<{ url: string; expires_at: string }> => (await api.post(`/machines/${id}/sol`, {})).data,
  // the emergency login of the installed system: owners, every view audited
  osPassword: async (id: string): Promise<{ user: string; password: string; set_at?: string; set_by?: string }> =>
    (await api.get(`/machines/${id}/os-password`)).data,
  setOSPassword: async (id: string, user: string, password?: string): Promise<{ user: string; set_at: string; generated: boolean }> =>
    (await api.post(`/machines/${id}/os-password`, { user, password })).data,
}

// one login try of a bmc-login-check job (result.checks)
export interface BMCLoginCheck {
  name: 'redfish' | 'web' | 'ipmi'
  ok: boolean
  refused?: boolean // the BMC refused the user or password
  skipped?: boolean // not tried: the web login on a BMC that is no AMI MegaRAC
  ms: number
  error?: string
  change_required?: boolean // the password is right, but the BMC demands a new one first
  account?: string // the account resource it names
}

export interface BMCCredentials {
  address: string
  username: string
  password: string
  spare_username?: string
  spare_password?: string
  spare_at?: string
}

// BMC operations, executed by a provisioner agent (job kind "bmc")
export interface BMCActionRequest {
  agent_id: string
  action:
    | 'bios-get' | 'bios-set' | 'bios-apply'
    | 'boot-get' | 'boot-order-set' | 'boot-first' | 'boot-option-set' | 'boot-override'
    | 'power-status' | 'power' | 'identify'
    | 'bmc-network-get' | 'bmc-interface-set' | 'bmc-log'
    | 'firmware-get' | 'firmware-update' | 'redfish-get' | 'console' | 'console-enable'
    | 'baseline-check' | 'baseline-apply'
    | 'bmc-accounts-get' | 'bmc-account-ensure' | 'bmc-login-check'
    | 'media-boot' | 'media-eject' // the maintenance ISO as the BMC's virtual CD (capability virtual-media)
  account?: string // bmc-account-ensure (default xcontrol)
  role?: 'Administrator' | 'Operator' | 'ReadOnly'
  attributes?: Record<string, unknown>
  order?: string[]
  power?: 'on' | 'off' | 'cycle' | 'soft'
  interface?: string
  enabled?: boolean
  option?: string // boot-first, boot-option-set
  device?: 'pxe' | 'disk' | 'bios' | 'cdrom' // boot-override: the next boot (IPMI boot flag)
  dry_run?: boolean // bios-apply
  limit?: number // bmc-log
  firmware_id?: number // firmware-update
  force?: boolean // firmware-update: reinstall the same version
  reboot?: boolean // firmware-update of a BIOS: power cycle and wait until it runs
  slot?: 'inactive' | 'active' // firmware-update of an AMI dual-image BMC
  path?: string // redfish-get
  seconds?: number // console
  baseline_id?: number // baseline-check/-apply (0: the baseline matching the machine)
}

// firmware-managed boot order kept in a BIOS attribute (AMI)
export interface FixedBootOrder {
  attribute: string
  entries: { name: string; reference: string; enabled: boolean }[]
  pending: boolean
}

export interface BIOSPlan {
  changes: { attribute: string; from: unknown; to: unknown }[]
  unchanged: number
  unknown?: string[]
}

export interface BMCLogEntry {
  created: string
  severity?: string
  message: string
  log: string
}

// a network interface of the BMC itself (dedicated port, shared NIC, host interface)
export interface BMCInterface {
  id: string
  name?: string
  mac?: string
  enabled?: boolean
  link?: string
  ipv4?: string[]
  vlan?: string
}

export interface BootOption {
  reference: string
  display_name: string
  device_path?: string
  enabled: boolean
}

export interface BootState {
  options: BootOption[]
  boot_next_supported: boolean
  order?: string[]
  override?: Record<string, unknown>
  fixed?: FixedBootOrder
}

export interface BIOSState {
  attributes: Record<string, unknown>
  pending?: Record<string, unknown>
  registry?: string
}

// JobView: a job in the list of all jobs
export interface JobView extends ProvisionJob {
  machine_name?: string
  cluster_id?: string
  action?: string // bmc/host action
}

export const provisionJobApi = {
  list: async (params: { kind?: string; state?: string; machine_id?: string; cluster_id?: string; active?: boolean; limit?: number } = {}): Promise<JobView[]> => {
    const response = await api.get<JobView[]>('/provision-jobs', { params: { ...params, active: params.active ? 1 : undefined } })
    return response.data
  },
  get: async (id: number, after = 0): Promise<{ job: ProvisionJob; events: ProvisionEvent[] }> => {
    const response = await api.get(`/provision-jobs/${id}`, { params: { after } })
    return response.data
  },

  cancel: async (id: number) => {
    const response = await api.post(`/provision-jobs/${id}/cancel`)
    return response.data
  },
}

// Certificates (Let's Encrypt via DNS-01)
export interface Certificate {
  id: number
  names: string
  staging: boolean
  status: string // pending, issuing, valid, failed
  cert_pem?: string
  not_after?: string
  issued_at?: string
  error?: string
  created_by: string
  created_at: string
}

export const certificateApi = {
  list: async (): Promise<{ certificates: Certificate[]; enabled: boolean; allowed_suffixes: string[] }> => {
    const response = await api.get('/certificates')
    return response.data
  },

  create: async (names: string[], staging: boolean): Promise<Certificate> => {
    const response = await api.post<Certificate>('/certificates', { names, staging })
    return response.data
  },

  renew: async (id: number) => {
    const response = await api.post(`/certificates/${id}/renew`)
    return response.data
  },

  bundle: async (id: number): Promise<{ names: string; cert_pem: string; key_pem: string }> => {
    const response = await api.get(`/certificates/${id}/bundle`)
    return response.data
  },

  delete: async (id: number) => {
    await api.delete(`/certificates/${id}`)
  },
}

// DNS records managed by xControl (below the configured zone)
export interface DNSRecord {
  id: number
  name: string
  type: string
  content: string
  ttl: number
  provider_id?: string
  machine_id?: string
  purpose: string // host, bmc, manual
  created_by: string
  updated_at: string
}

export interface ProviderRecord {
  id: string
  name: string
  type: string
  content: string
  ttl: number
  managed: boolean
}

export const dnsApi = {
  list: async (all = false): Promise<{
    enabled: boolean
    zone?: string
    records: DNSRecord[]
    provider_records?: ProviderRecord[]
    provider_error?: string
  }> => {
    const response = await api.get('/dns/records', { params: all ? { all: 1 } : {} })
    return response.data
  },

  create: async (data: { name: string; type: string; content: string; ttl?: number }): Promise<DNSRecord> => {
    const response = await api.post<DNSRecord>('/dns/records', data)
    return response.data
  },

  delete: async (id: number) => {
    await api.delete(`/dns/records/${id}`)
  },
}

// Networks of clusters (OOB, management, data) and their addresses
export interface Network {
  id: string
  site: string // the cluster id (historical name)
  cluster_id?: string
  site_id?: string // infrastructure networks (BMC, switch management, provisioning) belong to the site
  name: string
  purpose: string // oob-bmc, oob-switch, mgmt, mgmt-data, data, transfer, routed, interlink, storage
  mtu?: number // of the hosts' interfaces in it; none: 1500
  public_pool?: boolean // projects take public addresses for their VMs from it
  cidr: string
  vlan: number
  gateway?: string
  dns?: string
  dns_domain?: string
  alloc_from?: string
  closed: boolean
  provisioner_agent_id?: string
  notes?: string
  scope?: 'platform' | 'project'
  project_id?: string
  provider?: string // transfer: who is on the other side
  via?: string // routed: the transfer network it comes in over
  peer_asn?: number // transfer with BGP: the provider's AS
  local_asn?: number // transfer with BGP: ours
}

export interface NetworkAddress {
  id: number
  network_id: string
  ip: string
  machine_id?: string
  machine_name?: string
  purpose: string
  description?: string
}

export const networkApi = {
  list: async (site?: string): Promise<Network[]> => {
    const response = await api.get<Network[]>('/networks', { params: site ? { site } : {} })
    return response.data
  },
  create: async (n: Partial<Network>): Promise<Network> => {
    const response = await api.post<Network>('/networks', n)
    return response.data
  },
  update: async (n: Network): Promise<Network> => {
    const response = await api.put<Network>(`/networks/${n.id}`, n)
    return response.data
  },
  delete: async (id: string) => {
    await api.delete(`/networks/${id}`)
  },
  addresses: async (id: string): Promise<NetworkAddress[]> => {
    const response = await api.get<NetworkAddress[]>(`/networks/${id}/addresses`)
    return response.data
  },
  reserve: async (id: string, a: { ip: string; purpose?: string; description?: string }) => {
    const response = await api.post(`/networks/${id}/addresses`, a)
    return response.data
  },
  release: async (id: string, aid: number) => {
    await api.delete(`/networks/${id}/addresses/${aid}`)
  },
  nextIP: async (id: string): Promise<{ ip: string; prefix: number; gateway?: string; dns?: string[]; dns_domain?: string }> => {
    const response = await api.get(`/networks/${id}/next-ip`)
    return response.data
  },
}

export const healthCheck = async (): Promise<{ status: string }> => {
  const response = await api.get('/health')
  return response.data
}

export default api

// Central firmware repository: images stored once (by SHA-256), fetched by
// provisioners for BMC/BIOS updates
export interface FirmwareImage {
  id: number
  component: 'bmc' | 'bios' | 'nic' | 'nvme' | 'pdu'
  vendor: string
  models: string
  version: string
  min_version?: string // must be installed first (stepwise updates)
  fixes?: string // security issues fixed by this version, comma separated (CVE ids)
  filename: string
  sha256: string
  size: number
  source_url?: string
  notes?: string
  line?: string // image line, e.g. the BIOS line of a CPU generation (KMPA-U16-R)
  cpus?: string // CPU generations the image supports, comma separated (Rome,Milan)
  requires?: string // other components first, e.g. bmc>=1.02.32
  released_at?: string // vendor release date
  approved: boolean
  created_by: string
  created_at: string
  // the image's own fields merged with the dependency rules (as the update planning uses them)
  effective_min_version?: string
  effective_requires?: string
  rules?: number[] // dependency rules that apply to the image
}

export type DependencyKind = 'step' | 'requires' | 'tool' | 'no-downgrade' | 'block' | 'note'
export type DependencyConfidence = 'vendor' | 'tested' | 'reported' | 'open'

// One rule of the firmware knowledge base (GET /firmware-dependencies)
export interface FirmwareDependency {
  id: number
  component: FirmwareImage['component']
  vendor?: string
  models?: string // devices, comma separated; trailing * = prefix (14e4:1657:*)
  line?: string
  kind: DependencyKind
  from?: string // versions the rule is about: to install (step, requires, tool), installed (no-downgrade, block) or either (note)
  until?: string // exclusive
  needs?: string // step: version first; requires: bmc>=1.02.32; tool: niccli>=228.0.132.0
  description: string
  source?: string
  source_url?: string
  confidence: DependencyConfidence
  created_by: string
  created_at: string
  updated_at: string
  summary: string
  images?: number[] // repository images it applies to
  missing?: boolean // step: the version to install first is not in the repository
}

export type FirmwareDependencyInput = Omit<FirmwareDependency, 'id' | 'created_by' | 'created_at' | 'updated_at' | 'summary' | 'images' | 'missing'>

// A dependency set on an image itself (min_version, requires, CPU generations)
export interface ImageDependency {
  image_id: number
  component: FirmwareImage['component']
  vendor?: string
  models?: string
  line?: string
  version: string
  kind: 'step' | 'requires' | 'cpu'
  summary: string
  approved: boolean
}

export interface FirmwareDependencies {
  rules: FirmwareDependency[]
  from_images: ImageDependency[]
  kinds: DependencyKind[]
  confidences: DependencyConfidence[]
}

export const firmwareDependencyApi = {
  list: async (): Promise<FirmwareDependencies> => (await api.get<FirmwareDependencies>('/firmware-dependencies')).data,
  create: async (d: FirmwareDependencyInput): Promise<FirmwareDependency> => (await api.post<FirmwareDependency>('/firmware-dependencies', d)).data,
  update: async (id: number, d: FirmwareDependencyInput): Promise<FirmwareDependency> => (await api.put<FirmwareDependency>(`/firmware-dependencies/${id}`, d)).data,
  remove: async (id: number) => (await api.delete(`/firmware-dependencies/${id}`)).data,
}

export interface FirmwareMeta {
  component: string
  vendor?: string
  models?: string
  version: string
  min_version?: string
  fixes?: string
  notes?: string
  source_url?: string
  sha256?: string
  extract?: string // zip archives: file to take out (empty = automatic, "none" = keep the archive)
  line?: string
  cpus?: string
  requires?: string
  released_at?: string // YYYY-MM-DD
}

// Work the controller does in the background for a person (firmware imports,
// the vendor check): everyone sees it running and how it ended
export interface BackgroundTask {
  id: number
  kind: 'firmware-import' | 'vendor-check' | 'vendor-import'
  ref?: string
  title: string
  state: 'running' | 'done' | 'failed'
  step?: string
  progress: number // percent, -1 unknown
  result?: string
  error?: string
  by?: string
  started_at: string
  finished_at?: string
}

export const tasksApi = {
  // running ones and those that ended in the last 30 minutes, newest first
  list: async (): Promise<BackgroundTask[]> => (await api.get('/tasks')).data,
}

// our own packages for the hosts (VPP), as sets of files
export interface HostPackageFile { id: number; filename: string; sha256: string; size: number }
export interface HostPackageSet { name: string; version: string; approved: boolean; approved_by?: string; approved_at?: string; files: HostPackageFile[] }
export const hostPackageApi = {
  list: async (): Promise<HostPackageSet[]> => (await api.get<HostPackageSet[]>('/host-packages')).data,
  upload: async (name: string, version: string, file: File) => {
    const form = new FormData()
    form.append('name', name)
    form.append('version', version)
    form.append('file', file)
    return (await api.post('/host-packages', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },
  approve: async (name: string, version: string) => (await api.post(`/host-packages/${encodeURIComponent(name)}/${encodeURIComponent(version)}/approve`)).data,
}

export const firmwareApi = {
  list: async (): Promise<FirmwareImage[]> => (await api.get<FirmwareImage[]>('/firmware')).data,
  // onProgress: the share uploaded so far (0-1) - the upload itself runs in this browser
  upload: async (file: File, meta: FirmwareMeta, onProgress?: (share: number) => void): Promise<FirmwareImage> => {
    const form = new FormData()
    form.append('file', file)
    Object.entries(meta).forEach(([k, v]) => v !== undefined && v !== '' && form.append(k, String(v)))
    return (await api.post<FirmwareImage>('/firmware', form, { timeout: 0, onUploadProgress: (e) => e.total && onProgress?.(e.loaded / e.total) })).data
  },
  // the controller downloads in the background (a task)
  import: async (url: string, meta: FirmwareMeta): Promise<BackgroundTask> =>
    (await api.post<BackgroundTask>('/firmware/import', { ...meta, url })).data,
  update: async (id: number, data: Partial<Pick<FirmwareImage, 'approved' | 'models' | 'notes' | 'vendor' | 'version' | 'min_version' | 'fixes' | 'line' | 'cpus' | 'requires' | 'released_at'>>): Promise<FirmwareImage> =>
    (await api.put<FirmwareImage>(`/firmware/${id}`, data)).data,
  remove: async (id: number) => (await api.delete(`/firmware/${id}`)).data,
  vendor: async (): Promise<VendorFirmware> => (await api.get<VendorFirmware>('/firmware/vendor')).data,
  // both run on the controller as tasks
  vendorCheck: async (): Promise<BackgroundTask> => (await api.post<BackgroundTask>('/firmware/vendor/check', {})).data,
  vendorImport: async (id: number): Promise<BackgroundTask> =>
    (await api.post<BackgroundTask>(`/firmware/vendor/${id}/import`, { accept_terms: true })).data,
}

// The vendor check: what the vendors' support sites publish for the fleet's
// server models (weekly), placed against the repository
export interface VendorFirmwareWatch {
  id: number
  model: string
  vendor?: string
  found: boolean
  checked_at?: string
  error?: string
}

export interface VendorFirmwareRelease {
  id: number
  vendor: string
  model: string
  component: 'bmc' | 'bios'
  version: string
  vendor_version: string
  line?: string
  board?: string
  cpus?: string
  title?: string
  notes?: string
  released_at?: string
  url: string
  sha256?: string
  size?: string
  image_id?: number
  first_seen_at: string
  last_seen_at: string
  // imported: in the repository; new: newer than its images of the model and line
  state: 'imported' | 'new' | 'older'
  in_use: boolean
  requires?: string
}

export interface VendorFirmware {
  sources: string[]
  watches: VendorFirmwareWatch[]
  releases: VendorFirmwareRelease[]
}

export interface UpdateInfo {
  multipart_push_uri?: string
  push_uri?: string
  simple_update: boolean
  protocols?: string[]
  firmware: { id: string; name?: string; version: string; updateable: boolean; path: string }[]
}

export interface ConsoleCapture {
  screen: string
  text: string
  bytes: number
}

// one entry of a BMC credential import (never contains the password)
export interface BMCImportItem {
  line: number
  address: string
  username: string
  name?: string
  action: 'create' | 'update' | 'unchanged' | 'skip'
  reason?: string
  machine_id?: string
  role?: string
  host_macs?: string[]
  host_macs_from_switch?: boolean
  agent_id?: string
  inspect_job?: number
  result?: string
  mac?: string
  node?: string // switch port according to the file
  switch_ports?: string[] // where the switches see the BMC
  checks?: string[] // differences between file and discovery
  suggested?: boolean // name suggested by the naming scheme, not from the file
}

// installed firmware of a component compared with the newest approved image
export interface FirmwareState {
  component: 'bmc' | 'bios' | 'nic' | 'nvme'
  device?: string
  ports?: string[] // a NIC with several ports: they share one flash, updating device updates them all
  card?: string // its PCI address
  staged?: string // flashed, active after the next boot of the host
  model?: string
  installed: string
  latest?: string
  latest_id?: number
  update_available: boolean
  security?: boolean // a newer approved image fixes security issues
  fixes?: string[]
  note?: string
  line?: string // line of the offered image
  installed_released?: string // release date of the installed version
  latest_released?: string
  match?: string[] // why the offered image fits (model, CPU generation, line, exact device)
  requires?: string[] // components to update first ("BMC >= 1.02.32 first (installed 1.02.27)")
  source?: string // bios: "host" (reported by the BIOS at boot) or "redfish" (can be stale); nic/nvme: "bmc" (no agent on the host)
  pending?: string // newer fitting image that waits for approval
  pending_released?: string
  steps?: string[] // intermediate versions on the way to latest
  blocked?: string // a dependency rule forbids flashing this device
  unseen?: string // the BMC reports the card, the host has no network device for it - why
  rules?: RuleHint[] // dependency rules about this device and its offered version
  images?: { id: number; version: string; approved: boolean; released_at?: string; line?: string }[] // every fitting repository image, newest first
}

export interface RuleHint {
  id: number
  kind: DependencyKind
  summary: string
  description: string
  confidence?: DependencyConfidence
  source?: string
  source_url?: string
}

// Target profile for BMC and firmware settings of a hardware family
export interface BaselineSpec {
  bmc: { shared_nic?: string; host_interface?: string; sol?: string; preserve_config?: string }
  bios?: Record<string, unknown>
  boot: { no_data_nic_boot?: boolean }
}

export interface Baseline {
  id: number
  name: string
  models: string
  description?: string
  spec: string // JSON of BaselineSpec
  created_by: string
  updated_at: string
}

export interface BaselineItem {
  area: string
  item: string
  expected: string
  actual: string
  ok: boolean
  fixed?: boolean
  note?: string
}

export const baselineApi = {
  list: async (): Promise<Baseline[]> => (await api.get<Baseline[]>('/baselines')).data,
  save: async (b: { id?: number; name: string; models: string; description?: string; spec: BaselineSpec }): Promise<Baseline> =>
    b.id ? (await api.put<Baseline>(`/baselines/${b.id}`, b)).data : (await api.post<Baseline>('/baselines', b)).data,
  remove: async (id: number) => (await api.delete(`/baselines/${id}`)).data,
}

// A physical location: a datacenter (clusters stand there, several or legs
// of multi-site ones) or a warehouse (machines in stock: no power, no
// network - registered and moved by hand, no jobs)
// A rack at a site; machines take its height units (counted from the bottom)
export interface Rack {
  id: string
  site_id: string
  name: string
  units: number
  room?: string // the room of the site it stands in
  row?: string // the rack row within the room
  description?: string
}

// A PDU of a rack: the machines' power supplies hang on its outlets.
export interface PDUOutlet {
  number: number
  addr?: string
  label?: string
  phase?: number
  breaker?: string // the MCB it hangs on
  state?: 'on' | 'off' | ''
  switch?: string
  current_a?: number
  power_w?: number
  energy_kwh?: number
  power_factor?: number
  status?: string
  values?: Record<string, number | string>
  machine_id?: string
  machine_name?: string
  psu?: string
  note?: string
  lock?: string // set by a person: why never switched off
  locked?: string // why xControl never switches it off (the lock, or the machine: protected, switch, provisioner)
}

// a measured part of a PDU: inlet, phase, breaker (MCB), residual current monitor (RCM)
export interface PDUElement { name: string; address: string; phase?: number; values?: Record<string, number | string> }

export interface PDUReading {
  name?: string // the PDU's own name
  label?: string
  current_a?: number
  power_w?: number
  voltage_v?: number // the phases' mean
  energy_kwh?: number
  frequency_hz?: number
  neutral_a?: number
  inlet?: PDUElement
  phases?: PDUElement[]
  breakers?: PDUElement[]
  rcms?: PDUElement[]
  values: number // how many measurements had a value
}

export interface RackPDU {
  id: string
  rack_id: string
  name: string
  side?: 'left' | 'right' | ''
  feed?: string
  driver: string
  address: string
  mac?: string
  model?: string
  serial?: string
  firmware?: string
  username?: string
  has_credentials: boolean
  password_set_at?: string
  login_ok_at?: string
  login_failed_at?: string
  login_error?: string
  read_at?: string
  read_error?: string
  // the last firmware update through xControl
  update_state?: '' | 'running' | 'done' | 'failed'
  update_step?: string
  update_progress?: number
  update_from?: string
  update_to?: string
  update_image_id?: number
  update_by?: string
  update_started_at?: string
  update_finished_at?: string
  update_error?: string
  backup_at?: string // the settings backup before the update
  // the last action on the PDU through xControl (read, login), shown on its card
  action_kind?: 'read' | 'login'
  action_state?: 'running' | 'done' | 'failed'
  action_step?: string
  action_by?: string
  action_started_at?: string
  action_finished_at?: string
  action_error?: string
  reading?: PDUReading
  outlets: PDUOutlet[]
  // the PDU profile that fits (pkg/platform pdus/*.yaml): its title and layers, what it lacks
  // and why (those buttons stay, disabled with the reason), the factory login's user
  platform: string
  layers: string[]
  lacking?: Partial<Record<PDUCapability, string>>
  factory_user?: string
  // whether this unit reports values per outlet ('' not read yet) - the model may have them while a unit does not
  outlet_values?: '' | 'reported' | 'not-reported'
}

export type PDUCapability = 'read' | 'switch' | 'outlet-values' | 'own-password' | 'firmware'

// A PDU's platform as a PDU profile resolves it
export interface PDUPlatform {
  identity: { name?: string; mac?: string; vendor?: string; model?: string; firmware?: string }
  layers: string[]
  title: string
  recognized_by?: 'name' | 'identification' | 'mac'
  driver?: string
  identify?: string
  factory_user?: string
  capabilities: PDUCapability[]
  notes?: string[]
}

// A device that looks like a PDU xControl does not have: in the site's PDU network or behind a
// switch port; never taken in by itself
export interface PDUCandidate {
  mac: string
  address?: string
  name?: string
  model?: string
  serial?: string
  firmware?: string
  sources: string[] // dhcp-lease, neighbor, lldp, probe, identification
  ports?: string[]
  unconfigured_ports?: string[] // the probe found it there: configure the port first
  last_seen?: string
  stale?: boolean
  platform: PDUPlatform
  adoptable: boolean
  not_adoptable?: string
  identify_error?: string
}

export interface PowerFeed { pdu_id: string; pdu_name: string; outlet: PDUOutlet }

// Switching a machine's power through its PDU outlets, run by the controller
export interface MachinePowerOp {
  id: number
  machine_id: string
  kind: 'off' | 'on' | 'cycle' | 'outlet-off' | 'outlet-on'
  state: 'running' | 'done' | 'failed'
  outlets?: string // "pdu-id/number", comma separated
  target?: string // one-outlet operations: the outlet in words
  step?: string
  seconds?: number
  on_at?: string // a cycle switches the power on again then
  steps?: string // one per line
  error?: string
  by?: string
  started_at: string
  finished_at?: string
}

// What speaks against a PDU's firmware update now (one side at a time,
// every machine on it powered through another PDU too)
export interface PDUUpdateCheck { pdu_id: string; image_id?: number; blockers: string[]; notes: string[]; redundant: string[] }

// Power over time: the mean (and highest) power per step; t in Unix ms
export interface PowerPoint { t: number; w: number; max: number }
export interface PowerSeries { key: string; label: string; pdu_id: string; outlet: number; points: PowerPoint[]; last_w?: number; energy_kwh?: number }
export type PowerRange = '24h' | '7d' | '30d' | '365d'
export interface PowerHistory {
  range: PowerRange
  step_s: number
  from: string
  to: string
  series: PowerSeries[]
  total: PowerPoint[] // the steps where every series has a value, summed
  energy_kwh?: number
  missing: string[] // what is not in it and why
}

export const pduApi = {
  site: async (siteId: string): Promise<RackPDU[]> => (await api.get<RackPDU[]>(`/sites/${siteId}/pdus`)).data,
  // PDUs found in the site's PDU network and behind its switch ports (asked for their identification, read only)
  candidates: async (siteId: string): Promise<PDUCandidate[]> => (await api.get<PDUCandidate[]>(`/sites/${siteId}/pdu-candidates`, { timeout: 30000 })).data,
  platform: async (id: string): Promise<{ platform: PDUPlatform; lacking: Partial<Record<PDUCapability, string>>; outlet_values: RackPDU['outlet_values'] }> =>
    (await api.get(`/pdus/${id}/platform`)).data,
  save: async (p: Partial<RackPDU> & { name: string }, rackId?: string): Promise<RackPDU> =>
    rackId ? (await api.post<RackPDU>(`/racks/${rackId}/pdus`, p)).data : (await api.put<RackPDU>(`/pdus/${p.id}`, p)).data,
  remove: async (id: string) => (await api.delete(`/pdus/${id}`)).data,
  // xControl logs in right away; secure (default) gives the user a random password of its own
  // stored at once; login, own password and reading run on the controller (the PDU's action_* show it)
  setCredentials: async (id: string, body: { username: string; password: string; secure?: boolean }): Promise<RackPDU> =>
    (await api.put(`/pdus/${id}/credentials`, body, { timeout: 180000 })).data,
  credentials: async (id: string): Promise<{ address: string; username: string; password: string; set_at?: string }> => (await api.get(`/pdus/${id}/credentials`)).data,
  read: async (id: string): Promise<RackPDU> => (await api.post<RackPDU>(`/pdus/${id}/read`, {})).data, // runs on the controller
  // what runs on the PDUs (read, login, firmware update)
  running: async (): Promise<{ pdu_id: string; rack_id: string; name: string; what: string; step?: string; by?: string; started_at?: string }[]> => (await api.get('/pdus/running')).data,
  setOutlet: async (id: string, n: number, body: { machine_id?: string; psu?: string; note?: string; lock?: string }) => (await api.put(`/pdus/${id}/outlets/${n}`, body)).data,
  // a recorded operation of the outlet's machine, run on the controller
  switchOutlet: async (id: string, n: number, state: 'on' | 'off', confirm?: string): Promise<MachinePowerOp> => (await api.post(`/pdus/${id}/outlets/${n}/switch`, { state, confirm })).data,
  feeds: async (machineId: string): Promise<PowerFeed[]> => (await api.get<PowerFeed[]>(`/machines/${machineId}/power-feeds`)).data,
  firmware: async (id: string, imageId?: number): Promise<{ images: FirmwareImage[]; check: PDUUpdateCheck }> =>
    (await api.get(`/pdus/${id}/firmware`, { params: imageId ? { image_id: imageId } : {} })).data,
  // runs in the background on the controller; the PDU shows its progress
  startUpdate: async (id: string, imageId: number, confirm: string): Promise<RackPDU> => (await api.post(`/pdus/${id}/firmware`, { image_id: imageId, confirm })).data,
  rackPower: async (rackId: string, range: PowerRange): Promise<PowerHistory> => (await api.get(`/racks/${rackId}/power-history`, { params: { range } })).data,
  machinePower: async (machineId: string, range: PowerRange): Promise<PowerHistory> => (await api.get(`/machines/${machineId}/power-history`, { params: { range } })).data,
  // cuts the machine's power on all its outlets, waits, switches them on again
  // both run on the controller: the answer is the operation, the machine shows it running
  powerCycle: async (machineId: string, confirm: string, seconds?: number): Promise<MachinePowerOp> =>
    (await api.post(`/machines/${machineId}/power-feeds/cycle`, { confirm, seconds })).data,
  // every outlet of the machine off (confirm: its name) or on
  switchFeeds: async (machineId: string, state: 'on' | 'off', confirm?: string): Promise<MachinePowerOp> =>
    (await api.post(`/machines/${machineId}/power-feeds/switch`, { state, confirm })).data,
  powerOps: async (): Promise<MachinePowerOp[]> => (await api.get('/power-ops')).data,
}

// The network of a site: switches with their ports, every cable, the devices outside
export type Plane = '' | 'mgmt' | 'bmc' | 'switch' | 'trunk' | 'data' | 'peer' | 'external'

export interface PlanPort {
  port: string
  link?: 'up' | 'down' | 'admin-down'
  speed?: string
  plane?: Plane // '' or unset: not configured
  untagged_vlan?: number
  tagged_vlans?: string
  bond?: string
  description?: string
  neighbor?: string // LLDP: a switch or an outside device
  devices?: PortDevice[]
  findings?: string[]
  mode?: string // NotConfigured: administratively down
  link_since?: string
  link_changed_at?: string
  rx_packets?: number
  tx_packets?: number
}

export interface PlanSwitch {
  name: string
  address?: string
  role?: string // sw-mgmt, sw-data
  pair?: string // the MLAG partner
  machine_id?: string
  ports: PlanPort[]
}

export interface PlanEnd {
  kind: 'machine' | 'switch' | 'outside'
  id: string // machine ID, switch name, outside device name
  name: string
  port?: string // the NIC or the switch port
}

export interface PlanLink {
  a: PlanEnd // the machine (or a switch)
  b: PlanEnd // the switch port or the outside device
  plane: Plane
  up: boolean
  speed?: string
  bond?: string
  bmc?: boolean // the machine end is its BMC
  shared?: boolean // ... on the host port it shares
  source: 'switch' | 'host' | 'target' // the switches see it, the machine's agent heard the other end, or it is planned
  at?: string // host: heard last then (not now)
  planned?: 'new' | 'move' | 'keep' // target view: a planned cable, or one that must stay
  note?: string // target view: the plan's note
  // the machine end as its agent reported it last (also while the machine is off)
  nic?: NICConnection & { card?: string }
  host_at?: string
  host_from?: string // the agent: <host>-maint is the maintenance system
}

// The planned state of a site's infrastructure: devices, cables, steps of the rebuild
export type TargetKind = 'access' | 'agent' | 'router' | 'switch' | 'workload' | 'none'

export interface TargetDevice {
  machine_id: string
  kind: TargetKind
  label?: string // e.g. "OOB router B"
  change?: string // what changes on the way there
  keep?: boolean // must not change (e.g. the only way in)
}

export interface TargetEnd {
  kind: 'machine' | 'switch' | 'outside'
  id: string // machine ID, switch name, outside device name
  port?: string
}

export interface TargetLink {
  a: TargetEnd
  b: TargetEnd
  plane?: Plane
  speed?: string
  action: 'new' | 'move' | 'remove' | 'keep'
  from?: TargetEnd // move: where the first end is today
  note?: string
}

export interface SiteTarget {
  site_id?: string
  devices: TargetDevice[]
  links: TargetLink[]
  steps: { text: string; done?: boolean }[]
  updated_by?: string
  updated_at?: string // the version a change builds on
}

// A gateway's routing configuration rendered from xControl's data and compared
// with what it runs (lines separated by newlines)
export interface GatewayImageView {
  os?: string
  images?: { name: string; default: boolean; running: boolean }[]
  // signed: the trusted comment of biOS's signature over latest.json, checked by the controller
  latest?: { version: string; url: string; sha256: string; size: number; date: string; note: string; signature?: string; signed?: string }
  latest_error?: string
  error?: string
}

export interface GatewayConfig {
  machine_id: string
  rendered: string
  missing?: string // rendered, not running
  extra?: string // running, not rendered
  error?: string
  read_at: string
}

// One check of a site (docs/AUTOMATION.md, checks): its latest result and since
// when it is in that state
// The agent cluster of a site (docs/SITE-AGENTS.md)
export interface SiteRoleState {
  agent_id: string
  machine_id: string
  generation: string
  holds: string // the service addresses on the host, comma-separated
  bond_active: string
  bond_ports: string // "name up", comma-separated
  keepalived: string
  etcd: string
  etcd_healthy: boolean
  etcd_leader: boolean
  etcd_raft_index: number
  etcd_error?: string
  dnsmasq?: string
  public_key?: string
  certificate?: string
  relay_upstream?: string // the way out it uses now
  relay_down?: string // those that do not answer
  reported_at: string
}

export interface DefaultNetworks {
  base: string
  planes: { name: string; purpose: string; cidr: string; vlan: number; switch_vlan: number; note: string; state: 'present' | 'missing' | 'differs'; existing?: string }[]
}

export interface AgentSwitchPort {
  nic: string
  mac: string
  switch?: string
  port?: string
  untagged?: number
  tagged?: string
  state: 'ok' | 'configure' | 'unknown' | 'foreign'
  why?: string
}

export interface AgentTakeoverStep {
  key: string
  title: string
  state: string
  note?: string
  jobs?: Record<string, number>
  started_at?: string
  finished_at?: string
}

export interface AgentTakeoverView {
  stage: number
  blocked?: string
  temporary?: { agent: string; machine: string; protected: boolean }
  uplink?: { agent: string; machine: string }
  run?: { id: number; state: string; hold_before?: string; test_machine?: string; error?: string; created_by: string; steps: AgentTakeoverStep[] }
}

export interface AgentClusterMember {
  machine_id: string
  name: string
  own: Record<string, string> // plane -> its own address
  ports: string[] | null
  priority: number
  preferred: boolean
  generation?: string // what is rendered
  files?: { path: string; mode: string; content: string }[]
  render_error?: string
  online: boolean
  version?: string
  power?: string
  protected?: boolean
  state?: SiteRoleState
  fresh: boolean
  applied: boolean
  switch_ports?: AgentSwitchPort[]
}

export interface AgentCluster {
  error?: string
  preferred?: string
  gateway?: string
  planes?: { purpose: string; cidr: string; vlan: number; service: string }[]
  members: AgentClusterMember[]
  mode?: 'single' | 'multi' // one agent, or three
  marked?: number // machines with the role agent
  three_blocked?: string // why three cannot be chosen
  one_blocked?: string
  to_install?: string[] // machines with the role and no running system
  relay_upstreams?: string // the gateways with a relay
}

export interface SiteCheck {
  id?: number
  site_id: string
  key: string
  kind: 'reach' | 'bgp' | 'mlag' | 'ntp' | 'agent' | string
  target: string
  label: string
  machine_id?: string
  ok: boolean
  detail?: string
  by?: string // the agent or "controller"
  checked_at: string
  since: string
  stale?: boolean // no result for a while: its checker stopped
}

export interface SitePlan {
  switches: PlanSwitch[]
  links: PlanLink[]
  outside: { name: string; descr?: string; mgmt_addr?: string }[]
}

export interface Site {
  id: string
  name: string
  kind?: 'datacenter' | 'warehouse'
  location?: string
  description?: string
  inspect_auto?: boolean // new machines (no role) are inspected automatically
  agent_stage?: number // how far the agent cluster took over from the temporary agent
}

// The inspection of a machine without role: identify, service account,
// BMC and BIOS firmware, BIOS setup, maintenance system, NIC/NVMe firmware, off
// A firmware run (kind firmware): the NIC and NVMe firmware of a machine without an agent -
// maintenance system, flash, off; the steps of later rounds carry the round ("maintenance#2")
export interface InspectionStep {
  key: string // identify, account, bmc, bios, bios-setup, netboot, maintenance, erase, host-firmware, power-off, cold
  title: string
  state: 'pending' | 'running' | 'done' | 'skipped' | 'failed'
  jobs?: number[]
  devices?: string[]
  note?: string
  started_at?: string
  finished_at?: string
}

export interface InspectionRun {
  id: number
  machine_id: string
  kind?: 'firmware'
  state: 'running' | 'done' | 'failed' | 'cancelled'
  steps: InspectionStep[]
  error?: string
  created_by: string
  created_at: string
  finished_at?: string
}

export interface InspectionView {
  run: InspectionRun | null
  can_start: boolean
  why?: string
  auto: boolean
}

// What a network boot of a machine still needs in its BIOS setup (from its last reading)
export interface NetbootPlan {
  ready: boolean
  changes?: { setting: { setting?: string; path?: string[]; value: string }; title: string; from?: string; to: string }[]
  notes?: string[]
  reading?: number
  error?: string
}

export const netbootApi = {
  plan: async (id: string): Promise<NetbootPlan> => (await api.get<NetbootPlan>(`/machines/${id}/netboot`)).data,
  prepare: async (id: string): Promise<{ plan: NetbootPlan; job?: ProvisionJob; message?: string }> =>
    (await api.post<{ plan: NetbootPlan; job?: ProvisionJob; message?: string }>(`/machines/${id}/netboot`, {})).data,
}

export const inspectionApi = {
  get: async (id: string): Promise<InspectionView> => (await api.get<InspectionView>(`/machines/${id}/inspection`)).data,
  start: async (id: string): Promise<InspectionView> => (await api.post<InspectionView>(`/machines/${id}/inspection`, {})).data,
  cancel: async (id: string): Promise<InspectionView> => (await api.post<InspectionView>(`/machines/${id}/inspection/cancel`, {})).data,
  retry: async (id: string): Promise<InspectionView> => (await api.post<InspectionView>(`/machines/${id}/inspection/retry`, {})).data,
  firmware: async (id: string): Promise<InspectionView> => (await api.post<InspectionView>(`/machines/${id}/firmware-run`, {})).data,
  setSiteAuto: async (siteId: string, auto: boolean): Promise<Site> => (await api.put<Site>(`/sites/${siteId}/inspection`, { auto })).data,
}

// Group of machines with a purpose, at one or more sites (multi-site clusters)
export interface Cluster {
  id: string
  name: string
  site_id?: string // the first of site_ids
  site_ids?: string[] // every site the cluster stands at
  type: 'virtualization' | 'ceph' | 'bare-metal' | 'management' | 'mixed'
  name_prefix?: string
  description?: string
  machines?: number
  networks?: number
  agents?: number
}

// Ceph on a cluster's compute nodes, made and run by the nodes' jobs - never its keys
export interface CephNodeRef {
  machine_id: string
  name: string
  mgmt: string // the mon's address
  storage: string // the OSDs' address
  no_mon?: boolean // added later: OSDs only
}

export interface CephCluster {
  cluster_id: string
  fsid: string
  pool: string
  size: number
  min_size: number
  osds_per_node: number
  pg_num: number // the pool's PGs at least
  state: 'new' | 'running'
  health?: string // what the last node job read from `ceph -s`
  health_at?: string
  created_by: string
  created_at: string
  nodes: CephNodeRef[]
  jobs: ProvisionJob[] // each node's last ceph-node job
}

export const cephApi = {
  get: async (clusterId: string): Promise<CephCluster | null> => (await api.get<{ ceph: CephCluster | null }>(`/clusters/${clusterId}/ceph`)).data.ceph,
  // the first call makes the cluster with these nodes, later calls run the same nodes' jobs
  // again; nodes given beyond them join with OSDs only
  setup: async (clusterId: string, confirm: string, machineIds?: string[]): Promise<void> => {
    await api.post(`/clusters/${clusterId}/ceph`, { confirm, machine_ids: machineIds })
  },
}

export const clusterApi = {
  sites: async (): Promise<Site[]> => (await api.get<Site[]>('/sites')).data,
  // from: the site's id until now - another id in s renames it (every reference moves along)
  saveSite: async (s: Site, isNew: boolean, from?: string): Promise<Site> =>
    isNew ? (await api.post<Site>('/sites', s)).data : (await api.put<Site>(`/sites/${from || s.id}`, s)).data,
  removeSite: async (id: string) => (await api.delete(`/sites/${id}`)).data,
  // what the site's infrastructure has of what every datacenter needs
  readiness: async (siteId: string): Promise<ReadinessItem[]> => (await api.get<ReadinessItem[]>(`/sites/${siteId}/readiness`)).data,
  // the site's checks (reachability, BGP, MLAG, clocks, agents), failing first
  agentCluster: async (siteId: string): Promise<AgentCluster> => (await api.get<AgentCluster>(`/sites/${siteId}/agent-cluster`)).data,
  setAgentMode: async (siteId: string, mode: 'single' | 'multi', install: boolean): Promise<void> => {
    await api.put(`/sites/${siteId}/agent-cluster/mode`, { mode, install })
  },
  defaultNetworks: async (siteId: string): Promise<DefaultNetworks> => (await api.get<DefaultNetworks>(`/sites/${siteId}/default-networks`)).data,
  createDefaultNetworks: async (siteId: string, base: string): Promise<void> => {
    await api.post(`/sites/${siteId}/default-networks`, { base })
  },
  agentTakeover: async (siteId: string): Promise<AgentTakeoverView> => (await api.get<AgentTakeoverView>(`/sites/${siteId}/agent-takeover`)).data,
  startAgentTakeover: async (siteId: string, body: { test_machine?: string; hold_before?: string }): Promise<void> => {
    await api.post(`/sites/${siteId}/agent-takeover`, body)
  },
  continueAgentTakeover: async (siteId: string): Promise<void> => {
    await api.post(`/sites/${siteId}/agent-takeover/continue`, {})
  },
  cancelAgentTakeover: async (siteId: string): Promise<void> => {
    await api.post(`/sites/${siteId}/agent-takeover/cancel`, {})
  },
  agentTakeoverBack: async (siteId: string): Promise<void> => {
    await api.post(`/sites/${siteId}/agent-takeover/back`, {})
  },
  applyAgentCluster: async (siteId: string, machineId: string, dryRun: boolean): Promise<ProvisionJob> =>
    (await api.post<ProvisionJob>(`/sites/${siteId}/agent-cluster/apply`, { machine_id: machineId, dry_run: dryRun })).data,
  setAgentPreferred: async (siteId: string, preferred: string): Promise<void> => {
    await api.put(`/sites/${siteId}/agent-cluster`, { preferred })
  },
  siteChecks: async (siteId: string): Promise<{ interval_s: number; stale_after_s: number; checks: SiteCheck[] }> =>
    (await api.get(`/sites/${siteId}/checks`)).data,
  // NICs of all machines with link that are not connected to our infrastructure
  outsideNICs: async (): Promise<(NICConnection & { machine_id: string; machine: string; site_id?: string; role?: string })[]> =>
    (await api.get('/nic-connections/outside')).data,
  // where the NICs of every machine at the site are connected
  nicConnections: async (siteId: string): Promise<(NICConnections & { machine_id: string; machine: string })[]> =>
    (await api.get(`/sites/${siteId}/nic-connections`)).data,
  // the site's network: switches, ports, cables
  network: async (siteId: string): Promise<SitePlan> => (await api.get<SitePlan>(`/sites/${siteId}/network`)).data,
  target: async (siteId: string): Promise<SiteTarget> => (await api.get<SiteTarget>(`/sites/${siteId}/target`)).data,
  saveTarget: async (siteId: string, t: SiteTarget): Promise<SiteTarget> => (await api.put<SiteTarget>(`/sites/${siteId}/target`, t)).data,
  racks: async (siteId: string): Promise<Rack[]> => (await api.get<Rack[]>(`/sites/${siteId}/racks`)).data,
  saveRack: async (r: Partial<Rack> & { name: string }, siteId?: string): Promise<Rack> =>
    siteId ? (await api.post<Rack>(`/sites/${siteId}/racks`, r)).data : (await api.put<Rack>(`/racks/${r.id}`, r)).data,
  removeRack: async (id: string) => (await api.delete(`/racks/${id}`)).data,
  list: async (): Promise<Cluster[]> => (await api.get<Cluster[]>('/clusters')).data,
  save: async (c: Cluster, isNew: boolean): Promise<Cluster> =>
    isNew ? (await api.post<Cluster>('/clusters', c)).data : (await api.put<Cluster>(`/clusters/${c.id}`, c)).data,
  remove: async (id: string) => (await api.delete(`/clusters/${id}`)).data,
  // new machines and BMC care are set per site
  onboarding: async (siteId: string): Promise<SiteOnboarding> => (await api.get<SiteOnboarding>(`/sites/${siteId}/onboarding`)).data,
  saveOnboarding: async (siteId: string, o: { auto_adopt: boolean; bios_read?: boolean; username?: string; password?: string; clear?: boolean }): Promise<SiteOnboarding> =>
    (await api.put<SiteOnboarding>(`/sites/${siteId}/onboarding`, o)).data,
  bmcCare: async (siteId: string): Promise<BMCCare> => (await api.get<BMCCare>(`/sites/${siteId}/bmc-care`)).data,
  saveBMCCare: async (siteId: string, c: { health_hours: number; cold_reset: boolean; reset_weekday: number; reset_hour: number }): Promise<BMCCare> =>
    (await api.put<BMCCare>(`/sites/${siteId}/bmc-care`, c)).data,
}

// How a site takes in new machines: BMCs its provisioners discover become
// machines without role (and cluster) and are inspected with the onboarding
// credentials
export interface SiteOnboarding {
  site_id: string
  auto_adopt: boolean
  // read the BIOS setup of free machines whose BIOS version was not read yet
  bios_read: boolean
  bios_pending?: { machine_id: string; machine_name: string; bios_version: string; state: 'due' | 'running' | 'retry' | 'gave-up' | 'cancelled'; failures?: number; job_id?: number }[]
  since?: string // BMCs discovered before are left alone
  username?: string
  password_set: boolean
  updated_by?: string
  updated_at?: string
}

// Availability zones: changes that can disturb hosts run in one zone at a time
export interface ZoneActivity {
  zone: string
  what: string
  since: string
  until?: string // set while the zone soaks after a change
  session_id?: number // a maintenance session holds it
}

export interface Zone {
  id: string
  name: string
  cluster_id?: string
  site_id?: string
  description?: string
  soak_minutes: number
  implicit?: boolean // a cluster's machines outside any zone
  clusters: string[]
  machines: number
  maintenance: 'free' | 'running' | 'soaking'
  activities?: ZoneActivity[]
  can_start: boolean
  blocked_by?: ZoneActivity
}

export const zoneApi = {
  list: async (): Promise<Zone[]> => (await api.get<Zone[]>('/zones')).data,
  save: async (z: Partial<Zone>, isNew: boolean): Promise<Zone> =>
    isNew ? (await api.post<Zone>('/zones', z)).data : (await api.put<Zone>(`/zones/${z.id}`, z)).data,
  remove: async (id: string) => (await api.delete(`/zones/${id}`)).data,
  setClusterZones: async (clusterId: string, count: number) => (await api.put(`/clusters/${clusterId}/zones`, { count })).data,
  setMachineZone: async (machineId: string, zoneId: string) => (await api.put(`/machines/${machineId}/zone`, { zone_id: zoneId })).data,
}

// Maintenance sessions: who is at work on which machines, also between two jobs
export interface MaintenanceSession {
  id: number
  who: string // as people read it: Claude, mbosner, xControl
  by?: string // the account or tag behind it
  what: string
  explicit: boolean // started by hand, ends by hand
  started_at: string
  ended_at?: string
  ended_by?: string
  end_note?: string
  machines: { id: string; name: string; gone?: boolean; zone?: string }[]
  jobs: { id: number; machine_id: string; machine_name: string; kind: string; action?: string; state: string; created_by: string; created_at: string; finished_at?: string }[]
  why?: string[] // what keeps it open
  ends_at?: string // when it ends by itself if nothing changes
  can_end: boolean
  end_hint?: string
}

export const maintenanceSessionApi = {
  list: async (): Promise<MaintenanceSession[]> => (await api.get<MaintenanceSession[]>('/maintenance-sessions')).data,
  start: async (what: string, machineIds: string[]) => (await api.post('/maintenance-sessions', { what, machine_ids: machineIds })).data,
  // why: what became of the work (aborted, done, handed over), kept with the session
  end: async (id: number, why?: string) => (await api.post(`/maintenance-sessions/${id}/end`, { why })).data,
}

// realZone: the zone of a zone key - '' for the implicit maintenance scopes
// (a cluster without zone, a machine without cluster), which are no zones
export function realZone(key?: string): string {
  return key && !key.startsWith('cluster:') && !key.startsWith('machine:') ? key : ''
}

// zoneLabel: a zone key for people ("cluster:x" for a cluster without zone)
export function zoneLabel(key?: string): string {
  if (!key) return ''
  if (key.startsWith('cluster:')) return `${key.slice(8)} (outside zones)`
  if (key.startsWith('machine:')) return 'no cluster'
  return key
}

// Alerts ("Needs you"): derived from the current state by the controller
export interface Alert {
  key: string
  severity: 'error' | 'warning' | 'info'
  kind: 'machine-error' | 'zone-missing' | 'job-failed' | 'firmware' | 'os-updates' | 'bios-staged' | 'agent-offline' | 'agent-pending' | 'bmc-discovered' | 'bmc-login'
    | 'switch-port-new' | 'switch-port-config' | 'switch-port-unidentified' | 'switch-port-cabled' | 'pdu-candidate' | 'vendor-firmware'
    | 'bios-read' | 'boot-order' | 'bmc-health' | 'kvm-slow' | 'bmc-login-failed' | 'site-moved' | 'site-readiness' | 'link-speed'
  title: string
  detail?: string
  next?: string // what a person does about it
  retry?: boolean // job-failed: can be started again as it was
  machine_id?: string
  machine_name?: string
  agent_id?: string
  job_id?: number
  zone?: string
  since?: string
  protected?: boolean
  account?: string // bmc-login: the BMC account xControl logs in with
  device?: string // switch port alerts: the switch
  site_id?: string
  who: 'remote' | 'on-site' | 'waits' // who can deal with it
  state: string // fingerprint of what it says (snooze until it changes)
  snoozed_until?: string
  snoozed_by?: string
  snooze_note?: string
  snoozed_till_change?: boolean
  // it belongs to someone's maintenance session: shown as that, not counted as open
  expected?: { session_id: number; who: string; what: string; since: string }
}

export const alertApi = {
  list: async (all = false): Promise<Alert[]> => (await api.get<Alert[]>('/alerts', { params: all ? { all: true } : {} })).data,
  // state: the snooze holds only while the alert says the same (until it changes)
  snooze: async (key: string, hours: number, note = '', state?: string) => (await api.post(`/alerts/${encodeURIComponent(key)}/snooze`, { hours, note, state })).data,
  unsnooze: async (key: string) => (await api.delete(`/alerts/${encodeURIComponent(key)}/snooze`)).data,
}

export interface MachineUpdate extends Omit<FirmwareState, 'component'> {
  component: FirmwareState['component'] | 'os'
  machine_id: string
  machine_name: string
  cluster_id?: string
}

// Switch ports as reported by the provisioner agents (LLDP, VLANs)
export interface SwitchPort {
  id: number
  agent_id: string
  switch_name: string
  switch_address: string
  port: string
  untagged_vlan?: number
  tagged_vlans?: string // comma separated
  neighbor_name?: string
  neighbor_chassis?: string
  neighbor_port_id?: string
  neighbor_port_descr?: string
  neighbor_mgmt_ips?: string
  neighbor_is_bridge?: boolean
  link?: 'up' | 'down' | 'admin-down' // '' = not reported (older agents)
  speed?: string
  mode?: string
  master?: string // bond (or bridge) of the port
  description?: string
  lacp_partner?: string // bonds: the host's bond MAC
  lacp_bypass?: boolean // bonds: forwarding without LACP (the host runs none)
  rx_packets?: number // since the switch booted
  tx_packets?: number
  link_since?: string // since when xControl sees the link up (unset: up since before xControl watched)
  link_changed_at?: string
  updated_at: string
  switch_role?: string
  devices?: PortDevice[] // what is behind the port
  new?: boolean // link came up within the last 7 days
  findings?: PortFinding[]
  probe?: SwitchPortProbe // unconfigured ports: what the port saw while the probe had it up
  change?: PortChange // the port's newest switch job (one-click configuration)
}

// What an unconfigured port saw while the probe had it up (no bridge, no address)
export interface SwitchPortProbe {
  probed_at: string
  seconds: number
  link: boolean
  speed?: string
  neighbor_name?: string
  neighbor_chassis?: string
  neighbor_port_id?: string
  neighbor_mgmt_ips?: string
  macs?: string // comma separated, most frames first
}

export interface PortDevice {
  kind: 'bmc' | 'host' | 'switch' | 'pdu'
  shared?: boolean // a BMC on its shared channel (host LAN port)
  machine_id?: string
  machine_name?: string
  nic?: string
  mac?: string
  via: 'mac' | 'lldp' | 'lacp'
  label?: string
}

export interface PortFinding {
  kind: 'unidentified' | 'vlan' | 'pair' | 'cabled'
  severity: 'warning' | 'info'
  text: string
  suggest?: PortSuggestion // cabled: what xControl can configure itself (one click)
}

// A configuration xControl applies itself: the port as access port of vlan
export interface PortSuggestion {
  vlan: number
  like: string // e.g. "like swp35 (xcc1-new-02)"
}

// The newest switch job of a port: running, or finished within 24 hours
export interface PortChange {
  job_id: number
  state: 'queued' | 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled'
  dry_run?: boolean
  vlan: number
  bond?: string // the port became the member of this new bond
  like?: string
  by?: string
  at: string
  finished_at?: string
  error?: string
}

// Device types (chassis and slots) and the parts catalogue, filled from the inspections
export interface TypeSlot {
  name: string // e.g. PCIE1, OCP, NGFF1
  type?: string // e.g. PCI Express 4 x16
  length?: string
  note?: string
}

export interface DeviceType {
  id: string
  vendor?: string
  model?: string // as the machines report it (system product)
  name: string
  class: 'server' | 'router' | 'switch'
  units: number
  half_depth?: boolean
  sockets?: number
  memory_slots?: number
  slots: TypeSlot[]
  notes?: string
  source?: 'detected' | 'manual'
}

export interface DeviceTypeView extends DeviceType {
  machines: { id: string; name: string; site_id?: string }[]
  // per slot (and onboard): what the machines have in it, where it is empty, where it was not read yet
  slot_use: { slot: string; parts: { part_id?: string; name: string; machines: string[] }[]; empty: string[]; unknown: string[] }[]
}

export type PartKind = 'cpu' | 'nic' | 'disk' | 'memory' | 'transceiver' | 'cable' | 'other'

export interface Part {
  id: string
  kind: PartKind
  vendor?: string
  model?: string
  name: string
  match?: string // how the inventories name it (pci:…, cpu:…, disk:…, mem:…)
  ports?: number
  speed?: string
  media?: string
  size_gb?: number
  notes?: string
  source?: 'detected' | 'manual'
}

export interface PartView extends Part {
  used_in: { machine_id: string; machine: string; site_id?: string; slot?: string; count: number }[]
}

// The state of the platform at a glance (GET /overview)
export interface OverviewIssues { count: number; worst?: 'error' | 'warning' | 'info' }
export interface OverviewRack {
  id: string
  name: string
  room?: string
  row?: string
  units: number
  units_used: number
  machines: number
  machines_on: number
  pdus: number
  power_w?: number
  power_at?: string
  issues: OverviewIssues
}
export interface OverviewSite {
  id: string
  name: string
  kind: string
  location?: string
  rooms: string[]
  racks: OverviewRack[]
  machines: number
  machines_on: number
  clusters: number
  agents: number
  agents_online: number
  power_w?: number
  issues: OverviewIssues
}
export interface OverviewLoad { hosts: number; cpu_percent: number; memory_used_gb: number; memory_total_gb: number }
export interface OverviewCluster {
  id: string
  name: string
  compute: boolean
  machines: number
  machines_on: number
  threads: number
  memory_gb: number
  disk_tb: number
  vms: number
  vms_running: number
  host_load?: OverviewLoad // how busy its hosts are, as their agents report it
  vcpus_used: number
  memory_gb_used: number
  disk_tb_used: number
}
// A way out of a site as the network sees it: speed, next hop, how it is known
export interface SiteUplink {
  machine_id?: string
  machine: string
  port: string
  speed?: string
  up: boolean
  device: string // the device at the other end of the cable
  device_port?: string
  provider?: string
  circuit?: string
  next_hop?: string
  how?: 'bgp' | 'route'
  remote_as?: string
  session?: string
  ok: boolean
  since?: string
  prefixes_received?: number
  prefixes_sent?: number
  rx_bps?: number
  tx_bps?: number
}
export interface SiteSession { machine: string; peer: string; label?: string; session?: string; ok: boolean; since?: string }
export interface Overview {
  sites: OverviewSite[]
  sites_online: number
  datacenters: number
  racks: number
  machines: number
  machines_on: number
  machines_off: number
  agents: number
  agents_online: number
  power_w?: number
  host_load?: OverviewLoad
  vms: number
  vms_running: number
  clusters: OverviewCluster[]
  traffic: { uplinks: (SiteUplink & { site_id: string; site: string })[]; measured: number; rx_bps?: number; tx_bps?: number }
  maintenance: { open: number; remote: number; on_site: number; waiting: number; errors: number; expected: number; machines_with_updates: number; jobs_running: number; jobs_planned: number; next_window?: string }
}
export interface MaintenanceWindow { start: string; next: string; time_zone: string; automatic: boolean; updated_by?: string; updated_at?: string }

export const overviewApi = {
  get: async (): Promise<Overview> => (await api.get<Overview>('/overview')).data,
  uplinks: async (siteId: string): Promise<{ uplinks: SiteUplink[]; sessions: SiteSession[] }> => (await api.get(`/sites/${siteId}/uplinks`)).data,
  window: async (): Promise<MaintenanceWindow> => (await api.get<MaintenanceWindow>('/maintenance-window')).data,
  setWindow: async (start: string): Promise<MaintenanceWindow> => (await api.put<MaintenanceWindow>('/maintenance-window', { start })).data,
}

export interface LinkRule {
  kind: 'management' | 'data' | 'bmc'
  min_mbps: number // 0: the speed is not checked
  rated: boolean // also as fast as the card is rated, where the other end is one of our switches
  no_link: boolean // warn when the port has no link
  updated_by?: string
  updated_at?: string
}

// ProblemPlace: where one end of a network problem stands.
export interface ProblemPlace {
  site_id?: string
  site?: string
  rack_id?: string
  rack?: string
  u?: number
}

// NetworkProblem: a port or cable that needs a person (Network, Problems).
// NetworkProblemKind: what found it - the port rules (speed, no link), a link that comes and
// goes, a cable last seen at a switch port another machine has now, a port in use no switch hears
export type NetworkProblemKind = 'link-speed' | 'link-flap' | 'cable-moved' | 'no-switch' | 'slow-link'

export interface NetworkProblem {
  from_place?: ProblemPlace
  to_place?: ProblemPlace
  id: number
  key: string
  kind?: NetworkProblemKind
  site_id?: string
  machine_id?: string
  machine_name?: string
  nic?: string
  to?: string // the far end when a switch names it
  to_note?: string // ... or when only a person knows it
  state: string
  action: string
  cable?: string // what sits in the cage
  switch?: string
  switch_port?: string
  comment?: string
  fixed_by?: string
  fixed_at?: string
  check?: 'checking' | 'still'
  check_detail?: string
  check_job_id?: number // the port check that switches a port the host keeps off on to look
  cable_test?: string
  cable_at?: string
  first_seen: string
  resolved_at?: string // the port is fine: the row says thanks a moment, then it is gone
  waiting?: string // the machine is off or busy, so its port says nothing
  testable?: boolean // a copper port of a switch: its cable can be tested
  cable_running?: boolean
}

export const networkProblemApi = {
  list: async (site?: string): Promise<NetworkProblem[]> => (await api.get<NetworkProblem[]>('/network-problems', { params: { site: site || undefined } })).data,
  fixed: async (id: number): Promise<NetworkProblem> => (await api.post<NetworkProblem>(`/network-problems/${id}/fixed`, {})).data,
  cableTest: async (id: number) => (await api.post(`/network-problems/${id}/cable-test`, {})).data,
}

export const linkRuleApi = {
  list: async (): Promise<LinkRule[]> => (await api.get<LinkRule[]>('/link-rules')).data,
  update: async (r: LinkRule): Promise<LinkRule> => (await api.put<LinkRule>(`/link-rules/${r.kind}`, r)).data,
}

export const hardwareApi = {
  types: async (): Promise<DeviceTypeView[]> => (await api.get<DeviceTypeView[]>('/hardware/types')).data,
  saveType: async (t: DeviceType, isNew: boolean): Promise<DeviceType> =>
    isNew ? (await api.post<DeviceType>('/hardware/types', t)).data : (await api.put<DeviceType>(`/hardware/types/${t.id}`, t)).data,
  removeType: async (id: string) => (await api.delete(`/hardware/types/${id}`)).data,
  parts: async (): Promise<PartView[]> => (await api.get<PartView[]>('/hardware/parts')).data,
  savePart: async (p: Part, isNew: boolean): Promise<Part> =>
    isNew ? (await api.post<Part>('/hardware/parts', p)).data : (await api.put<Part>(`/hardware/parts/${p.id}`, p)).data,
  removePart: async (id: string) => (await api.delete(`/hardware/parts/${id}`)).data,
  detect: async (): Promise<{ types_added: number; parts_added: number }> => (await api.post('/hardware/detect', {})).data,
  setMachineType: async (machineId: string, typeId: string) => (await api.put(`/machines/${machineId}/device-type`, { device_type_id: typeId })).data,
}

export const switchApi = {
  list: async (): Promise<SwitchPort[]> => (await api.get<SwitchPort[]>('/switches')).data,
  // an unconfigured port as access port of vlan: a switch job (dry_run: only the plan)
  configurePort: async (name: string, port: string, body: { vlan: number; like?: string; dry_run?: boolean }): Promise<ProvisionJob> =>
    (await api.post<ProvisionJob>(`/switches/${encodeURIComponent(name)}/ports/${encodeURIComponent(port)}/configure`, body)).data,
  // the port as the member of a new bond of the machine behind it, written like the bond `like`
  makeBond: async (name: string, port: string, body: { machine_id: string; bond?: string; like: string; dry_run?: boolean }): Promise<ProvisionJob> =>
    (await api.post<ProvisionJob>(`/switches/${encodeURIComponent(name)}/ports/${encodeURIComponent(port)}/bond`, body)).data,
}

// ---- Inside a project (project area) ----
// Platform staff acting in a customer's project send a reason with every
// change; the project layout sets it, the server writes it to the audit log.
let projectReason = ''
export function setProjectReason(reason: string) {
  projectReason = reason
}
const inProject = axios.create({ baseURL: API_BASE, withCredentials: true, headers: { 'Content-Type': 'application/json', ...CSRF_HEADERS } })
inProject.interceptors.request.use((config) => {
  if (projectReason && config.method && config.method !== 'get') config.headers.set('X-XC-Reason', projectReason)
  return config
})
inProject.interceptors.response.use((r) => r, (error) => {
  if (error.response?.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
  return Promise.reject(error)
})
offerQueue(inProject)

export interface ServerView {
  id: string
  kind: 'dedicated' | 'vm'
  name: string
  state: string
  usage?: string
  size: string
  os?: string
  ips?: string[]
  own: boolean
  created_at: string
  actions: string[]
  // what went wrong; the users on a VM (xcontrol: the platform's, on every project VM)
  status?: string
  logins?: string[]
  // a VM's job on its host until it ends; queued: it waits for another job there
  job_state?: 'queued' | 'pending' | 'running'
}

// ProjectImage: an image a project can start a VM from.
export interface ProjectImage {
  id: string
  name: string
  os_distribution?: string
  os_version?: string
  min_disk_gb?: number
  size_bytes?: number
}

// ComputeView: a compute host's side of the VMs.
export interface ComputeView {
  host?: { machine_id: string; transit?: string; storage?: string; numa: number; ready: boolean; disks_at?: string; applied_at?: string; gpus_at?: string }
  cores_all: number
  cores_used: number
  vms: { id: string; name: string; state: string; status?: string; vcpus: number; memory_mb: number; disk_gb: number; public_ip?: string; project_id?: string; job_id?: number; gpus?: string[]; partitions?: number[] }[]
  gpu?: GPUHostState
  gpu_packages?: string
  gpu_no_packages?: string
}

// GPUHostState: what a GPU host reports - driver, Fabric Manager, nvlsm, its GPUs and NVLink partitions.
export interface GPUHostState {
  version?: string
  driver?: string
  fabric_manager?: string
  fabric_mode: number
  nvlsm?: string
  held?: string[]
  iommu_pt: boolean
  reboot?: string[]
  gpus: { pci: string; id: string; numa: number; driver?: string; module?: number }[]
  partitions: { id: number; active: boolean; gpus: string[]; modules: number[] }[]
  problems?: string[]
}

// VMNetworkView: a network a project made for its VMs, for the platform's admins.
export interface VMNetworkView {
  id: string
  project_id: string
  project_name?: string
  customer_id?: string
  customer_code?: string
  name: string
  kind: 'private' | 'public'
  cidr: string
  gateway?: string
  vni?: number
  pool_id?: string
  addresses: number
}

export const vmNetworkApi = {
  list: async (): Promise<VMNetworkView[]> => (await api.get<VMNetworkView[]>('/vm-networks')).data,
}

export interface ProjectNetwork {
  id: string
  name: string
  kind?: 'private' | 'public' // a network of its VMs; without: one the platform team set up
  cidr: string
  vlan: number
  gateway?: string
  dns?: string
  can_manage: boolean
  addresses: { id: number; ip: string; use: string; server?: string; description?: string }[]
}

export interface ProjectMember {
  id: number
  subject: string
  role: string
  created_by: string
  created_at: string
}

export interface ActivityItem {
  time: string
  actor: string
  platform: boolean
  what: string
  detail?: string
  ok: boolean
}

export interface SSHKey {
  id: number
  owner: string
  name: string
  public_key: string
  fingerprint: string
  created_at: string
}

export interface ProjectToken {
  id: number
  owner: string
  name: string
  prefix: string
  expires_at?: string
  last_used_at?: string
  created_at: string
}

// A volume on Ceph; shared: it may hang on several VMs at once (their guests need a file system made for that)
export interface ProjectVolume {
  id: string
  name: string
  size_gb: number
  shared: boolean
  state: 'creating' | 'available' | 'error' | 'deleting'
  status?: string
  created_by: string
  created_at: string
  attached: { id: string; name: string }[]
}

const P = (id: string) => `/projects/${encodeURIComponent(id)}`

export const projectAreaApi = {
  projects: async (): Promise<Project[]> => (await inProject.get('/projects')).data,
  createProject: async (customerId: string, data: { name: string; id?: string; description?: string }): Promise<Project> =>
    (await inProject.post('/projects', { ...data, customer_id: customerId })).data,
  project: async (id: string): Promise<Project> => (await inProject.get(P(id))).data,
  updateProject: async (id: string, data: { name?: string; description?: string }): Promise<Project> => (await inProject.put(P(id), data)).data,
  servers: async (id: string): Promise<ServerView[]> => (await inProject.get(`${P(id)}/servers`)).data,
  serverAction: async (id: string, server: string, action: string): Promise<{ job_id?: number; state: string }> =>
    (await inProject.post(`${P(id)}/servers/${encodeURIComponent(server)}/actions`, { action })).data,
  job: async (id: string, job: number): Promise<{ id: number; state: string; action: string; power?: string; phase?: string; error?: string }> =>
    (await inProject.get(`${P(id)}/jobs/${job}`)).data,
  // reinstall wipes the disk of a dedicated server and installs the platform's OS
  reinstall: async (id: string, server: string, data: { confirm: string; hostname: string; username: string; ssh_key_ids: number[]; network_id?: string }): Promise<{ id: number; state: string }> =>
    (await inProject.post(`${P(id)}/servers/${encodeURIComponent(server)}/reinstall`, data)).data,
  images: async (id: string): Promise<ProjectImage[]> => (await inProject.get(`${P(id)}/images`)).data,
  createVM: async (id: string, data: { name: string; vcpus: number; memory_mb: number; disk_size_gb: number; image_id: string; ssh_key_ids: number[]; volume_ids?: string[]; host?: string; gpus?: string[]; vlans?: { vlan: number; address: string }[] }) => (await inProject.post(`${P(id)}/vms`, data)).data,
  volumes: async (id: string): Promise<ProjectVolume[]> => (await inProject.get(`${P(id)}/volumes`)).data,
  createVolume: async (id: string, data: { name: string; size_gb: number; shared: boolean }): Promise<ProjectVolume> => (await inProject.post(`${P(id)}/volumes`, data)).data,
  deleteVolume: async (id: string, vol: string) => { await inProject.delete(`${P(id)}/volumes/${encodeURIComponent(vol)}`) },
  deleteVM: async (id: string, vm: string) => { await inProject.delete(`${P(id)}/vms/${encodeURIComponent(vm)}`) },
  networks: async (id: string): Promise<ProjectNetwork[]> => (await inProject.get(`${P(id)}/networks`)).data,
  createNetwork: async (id: string, data: { name: string; kind: string; cidr?: string }) => (await inProject.post(`${P(id)}/networks`, data)).data,
  deleteNetwork: async (id: string, net: string) => { await inProject.delete(`${P(id)}/networks/${encodeURIComponent(net)}`) },
  reserve: async (id: string, net: string, data: { ip: string; description: string }) => (await inProject.post(`${P(id)}/networks/${encodeURIComponent(net)}/addresses`, data)).data,
  release: async (id: string, net: string, addr: number) => { await inProject.delete(`${P(id)}/networks/${encodeURIComponent(net)}/addresses/${addr}`) },
  members: async (id: string): Promise<{ members: ProjectMember[]; can_manage: boolean }> => (await inProject.get(`${P(id)}/members`)).data,
  saveMember: async (id: string, data: { subject: string; role: string }) => (await inProject.post(`${P(id)}/members`, data)).data,
  removeMember: async (id: string, member: number) => { await inProject.delete(`${P(id)}/members/${member}`) },
  activity: async (id: string): Promise<ActivityItem[]> => (await inProject.get(`${P(id)}/activity`)).data,
  sshKeys: async (id: string): Promise<SSHKey[]> => (await inProject.get(`${P(id)}/ssh-keys`)).data,
  addSSHKey: async (id: string, data: { name: string; public_key: string }) => (await inProject.post(`${P(id)}/ssh-keys`, data)).data,
  deleteSSHKey: async (id: string, key: number) => { await inProject.delete(`${P(id)}/ssh-keys/${key}`) },
  tokens: async (id: string): Promise<ProjectToken[]> => (await inProject.get(`${P(id)}/tokens`)).data,
  createToken: async (id: string, data: { name: string; days: number }): Promise<{ token: string; id: number; name: string; expires_at: string }> =>
    (await inProject.post(`${P(id)}/tokens`, data)).data,
  deleteToken: async (id: string, token: number) => { await inProject.delete(`${P(id)}/tokens/${token}`) },
}

// firmwareOf returns the BMC or BIOS state of a machine from the machine list
export function firmwareOf(m: Machine, component: 'bmc' | 'bios'): FirmwareState | undefined {
  return (m.firmware || []).find((f) => f.component === component)
}

// Firmware rollouts: several machines one after the other, BMC then BIOS
export interface RolloutStep {
  machine_id: string
  machine_name: string
  component: 'bmc' | 'bios'
  image_id?: number
  from?: string
  to?: string
  job_id?: number
  state: 'planned' | 'running' | 'done' | 'skipped' | 'failed'
  note?: string
}

export interface FirmwareRollout {
  id: number
  components: string
  reboot: boolean
  state: 'running' | 'done' | 'failed' | 'cancelled'
  zone?: string // availability zone it runs in (holds that zone's lock)
  current: number
  error?: string
  created_by: string
  created_at: string
  steps: RolloutStep[]
}

export const rolloutApi = {
  list: async (): Promise<FirmwareRollout[]> => (await api.get<FirmwareRollout[]>('/firmware-rollouts')).data,
  get: async (id: number): Promise<FirmwareRollout> => (await api.get<FirmwareRollout>(`/firmware-rollouts/${id}`)).data,
  create: async (req: { machine_ids: string[]; components: string[]; reboot: boolean; dry_run?: boolean }): Promise<FirmwareRollout> =>
    (await api.post<FirmwareRollout>('/firmware-rollouts', req)).data,
  cancel: async (id: number) => (await api.post(`/firmware-rollouts/${id}/cancel`)).data,
}

// OS update rollouts: the operating systems zone after zone, machine after machine, through their agents
export interface OSRolloutStep {
  machine_id: string
  machine_name: string
  zone: string
  kind: 'update' | 'ceph-hold' | 'reboot' | 'ceph-release'
  state: 'planned' | 'waiting' | 'running' | 'done' | 'skipped' | 'refused' | 'failed'
  note?: string
  job_id?: number
  pending?: number
  security?: number
  held?: string[]
  reboot_required?: boolean
  kernel?: boolean
  vms?: number
  ceph_host?: string
}

export interface OSRollout {
  id: number
  mode: 'security' | 'full'
  reboot: boolean
  state: 'running' | 'done' | 'failed' | 'cancelled'
  zone?: string // the zone of the step it is at (holds that zone's lock)
  current: number
  error?: string
  created_by: string
  created_at: string
  steps: OSRolloutStep[] | null
}

export interface OSRolloutRequest { machine_ids?: string[]; cluster_id?: string; mode: 'security' | 'full'; reboot: boolean; dry_run?: boolean }

export const osRolloutApi = {
  list: async (): Promise<OSRollout[]> => (await api.get<OSRollout[]>('/os-rollouts')).data,
  create: async (req: OSRolloutRequest): Promise<OSRollout> => (await api.post<OSRollout>('/os-rollouts', req)).data,
  cancel: async (id: number) => (await api.post(`/os-rollouts/${id}/cancel`)).data,
  setHeld: async (machineId: string, packages: string[]) => (await api.put(`/machines/${machineId}/held-packages`, { packages })).data,
}

// ConsoleShot is a picture of a machine's screen through the BMC's KVM viewer,
// with the text OCR read from it.
export interface ConsoleShot {
  id: number
  machine_id: string
  state: 'running' | 'done' | 'failed'
  error?: string
  taken_by: string
  width?: number
  height?: number
  text?: string
  ocr_error?: string
  created_at: string
  finished_at?: string
}

// SetupSettingDef is a BIOS setting the setup automation may read and change.
// A complete reading of a machine's BIOS setup (every page and setting)
export interface BIOSSetupDump {
  id: number
  machine_id: string
  job_id: number
  bios_version?: string
  board?: string // board vendor and product ("BMC model ..." when unknown)
  cpu?: string // CPU generation, e.g. Milan
  seconds?: number // how long the reading took
  crawler_version?: number // version of the reading rules
  item_count: number
  error?: string // the reading stopped early
  created_by: string
  created_at: string
}

// One line of the setup: tab, submenus, item (a repeated label carries #n)
export interface AptioItem {
  path: string[]
  label: string
  value?: string
  kind: 'enum' | 'value' | 'submenu' | 'action' | 'info'
  options?: string[]
  help?: string
}

// One timed request to a BMC (tcp, redfish-root, redfish-system,
// redfish-manager, web-login, ipmi)
export interface BMCHealthCheck {
  name: string
  ms: number
  error?: string
}

export interface BMCHealthSample {
  id: number
  machine_id: string
  job_id: number
  at: string
  trigger: 'scheduled' | 'before-reset' | 'after-reset' | 'manual'
  total_ms: number
  failed: number
  checks: BMCHealthCheck[]
}

// A machine's BMC measurements and what is usual for it and its BMC model
export interface BMCHealth {
  status: 'ok' | 'slow' | 'failing' | 'unknown'
  usual_ms: number
  model_usual_ms: number
  checks_usual_ms?: Record<string, number>
  samples: BMCHealthSample[] | null
  last_reset?: string
  bmc_model?: string
  // the newest complete BIOS reading against its profile: the way through the KVM console
  kvm?: { dump_id: number; at: string; seconds: number; profile_seconds?: number; profile_readings: number; approximate?: boolean; status: 'ok' | 'slow' | 'unknown' }
}

// Time spent per page of a BIOS reading, with the usual time of its profile
export interface BIOSTimings {
  dump: BIOSSetupDump
  approximate: boolean // derived from the job log (older readings)
  seconds: number
  profile_seconds: number
  profile_readings: number
  status: 'ok' | 'slow' | 'unknown'
  pages: { path: string[]; seconds: number; open?: number; items: number; profile_seconds?: number }[]
}

// How xControl looks after the BMCs of a site: measurements, weekly cold resets
export interface BMCCare {
  site_id: string
  health_hours: number
  cold_reset: boolean
  reset_weekday: number // 0 Sunday
  reset_hour: number // UTC
  updated_by?: string
  updated_at?: string
  next_reset?: string
  machines: { machine_id: string; machine_name: string; protected?: boolean; status: BMCHealth['status']; latest_ms?: number; usual_ms?: number; latest_at?: string; last_reset?: string }[] | null
}

// A difference between two readings of a setup
export interface BIOSItemChange {
  path: string[]
  kind: AptioItem['kind']
  change: 'added' | 'removed' | 'changed' | 'moved'
  path_from?: string[] // the path before, when the item moved (a tab became a submenu, a tab was renamed)
  value?: string // after (added, changed), before (removed)
  value_changed?: boolean
  from?: string
  kind_from?: string
  options?: string[]
  options_added?: string[]
  options_removed?: string[]
  help_changed?: boolean
  help?: string
}

export interface BIOSDumpDiff {
  from: BIOSSetupDump
  to: BIOSSetupDump
  changes: BIOSItemChange[]
  counts: Record<string, number>
}

// The setup readings of one board, CPU generation and BIOS version
export interface BIOSProfile {
  id: string // board|cpu|version
  board: string
  cpu?: string
  bios_version: string
  machines: { machine_id: string; machine_name: string; dump_id: number; read_at: string; partial?: boolean; items: number }[]
  items: number
  settings: number
  deviations: number // settings the machines have differently
  read_at: string
}

// A setting as most machines of a profile have it; values when they differ
export interface BIOSProfileItem extends AptioItem {
  machines: number
  values?: { value: string; machines: string[] }[]
}

export const biosProfileApi = {
  list: async (): Promise<BIOSProfile[]> => (await api.get<BIOSProfile[]>('/bios-profiles')).data,
  items: async (id: string): Promise<{ profile: BIOSProfile; items: BIOSProfileItem[] }> =>
    (await api.get<{ profile: BIOSProfile; items: BIOSProfileItem[] }>('/bios-profiles/items', { params: { id } })).data,
  diff: async (from: string, to: string, q: { info?: boolean; help?: boolean } = {}): Promise<{ from: BIOSProfile; to: BIOSProfile; changes: BIOSItemChange[]; counts: Record<string, number> }> =>
    (await api.get('/bios-profiles/diff', { params: { from, to, ...q } })).data,
}

export interface SetupSettingDef {
  name: string
  title: string
  path: string[]
  values: string[] | null
  min?: number // a number field: min..max, no list
  max?: number
}

// Availability groups: what stands in for what at a site (docs/MACHINES.md)
export interface AvailabilityGroupMember {
  machine_id: string
  name: string
  up: boolean
  why?: string
  changing_job?: number
  settling?: boolean
}
export interface AvailabilityGroup {
  id: number
  site_id: string
  name: string
  kind: 'gateways' | 'agents' | 'custom'
  min_available: number // 0: all but the one being changed
  needed_up: number
  members: AvailabilityGroupMember[]
  change_one: boolean
  why?: string
}
export const availabilityGroupApi = {
  list: async (siteId: string): Promise<AvailabilityGroup[]> => (await api.get(`/sites/${siteId}/availability-groups`)).data,
  create: async (siteId: string, req: { name: string; min_available?: number; machine_ids: string[] }): Promise<AvailabilityGroup> =>
    (await api.post(`/sites/${siteId}/availability-groups`, req)).data,
  update: async (id: number, req: { name?: string; min_available?: number; machine_ids?: string[] }): Promise<AvailabilityGroup> =>
    (await api.put(`/availability-groups/${id}`, req)).data,
  remove: async (id: number): Promise<void> => {
    await api.delete(`/availability-groups/${id}`)
  },
}

// The access layer at a site (docs/NETBIRD.md)
export interface SiteOverlay {
  configured: boolean
  url: string
  enabled: boolean
  note?: string
  relays: boolean
  error?: string
  peers: { name: string; machine_id: string; enrolled: boolean; connected: boolean; address?: string; last_seen?: string }[]
  token?: { expires_at?: string; checked_at?: string; renewed_at?: string; error?: string }
}
export const overlayApi = {
  get: async (siteId: string): Promise<SiteOverlay> => (await api.get(`/sites/${siteId}/overlay`)).data,
  set: async (siteId: string, enabled: boolean): Promise<SiteOverlay> => (await api.put(`/sites/${siteId}/overlay`, { enabled })).data,
}
